import noop from 'lodash/noop';
import * as React from 'react';
import React__default, { createContext, useState, useContext, useMemo, useCallback, useEffect, useRef, forwardRef, useImperativeHandle, useReducer } from 'react';
import { useForm, useWatch as useWatch$1 } from 'react-hook-form';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { makeStyles, useTheme, createTheme, ThemeProvider } from '@material-ui/core/styles';
import CheckboxMaterial from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import clsx from 'clsx';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import merge from 'lodash/merge';
import { IMaskInput } from 'react-imask';
import { PrimaryButton } from '@acin.reactjs/buttons';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import mime from 'mime';
import { useDrop, DndProvider } from 'react-dnd';
import { NativeTypes, HTML5Backend } from 'react-dnd-html5-backend';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ArrowBack from '@material-ui/icons/ArrowBack';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Popper from '@material-ui/core/Popper';
import naturalCompare from 'natural-compare';
import { DatePicker as DatePicker$1, RangePicker as RangePicker$1, DateTimePicker as DateTimePicker$1, TimePicker as TimePicker$1 } from '@acin.reactjs/date-picker';
export { WrapperDatePicker } from '@acin.reactjs/date-picker';
import { useTheme as useTheme$1 } from '@material-ui/styles';
import { useUtils } from '@material-ui/pickers';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const FormContext = createContext({});

const defaultMessages = {
  minLength: "The field length is too short",
  maxLength: "The field length is too long",
  pattern: "The field has invalid characters",
  max: "The field value is too high",
  min: "The field value is too low",
  required: "The field is required",
  minDate: "The field value must meet the required minimum date",
  maxDate: "The field value must meet the required maximum date",
  invalidDate: "The date is not valid",
  maxFileSize: "Maximum file size of {{1}}kB exceeded",
  maxFilesSize: "The maximum files size combined of {{1}}kB exceeded",
  fileTypes: "Only the following file extensions are allowed: {{1}}",
  maxNumberFiles: "Maximum files exceeded. Only {{1}} files allowed",
  minDateTime: "The field value must meet the required minimum date time",
  maxDateTime: "The field value must meet the required maximum date time",
  invalidDateTime: "The date time is not valid",
  invalidTime: "The time is not valid",
  maxTime: "The field value must meet the required maximum time of {{1}}",
  minTime: "The field value must meet the required minimum time of {{1}}"
};

const noActionValidations = ["validate", "required"];
const isValidationObject = value => typeof value === "object" && "value" in value && "message" in value;
const validationMap = function (inputValidations) {
  let required = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let defaultMessages$1 = arguments.length > 2 ? arguments[2] : undefined;
  const validations = {};
  let preDefaultMessages = Object.assign(Object.assign({}, defaultMessages), defaultMessages$1);
  for (const validation in inputValidations) {
    if (noActionValidations.includes(validation)) {
      if (validation === "validate") {
        validations["validate"] = inputValidations["validate"];
      }
      continue;
    }
    const value = inputValidations[validation];
    const message = preDefaultMessages[validation];
    if (isValidationObject(value)) {
      setValueIntoObject(validations, validation, {
        value: value.value,
        message: value.message || message
      });
    } else {
      setValueIntoObject(validations, validation, {
        value,
        message
      });
    }
  }
  return Object.assign({
    required: required ? preDefaultMessages.required : false
  }, validations);
};
const getMessageWithArguments = function (message) {
  let finalMessage = message;
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }
  args.forEach((value, index) => {
    const regex = new RegExp(`\\{\\{${index + 1}\\}\\}`, "g");
    finalMessage = finalMessage.replace(regex, value);
  });
  return finalMessage;
};
const setValueIntoObject = (object, key, value) => {
  object[key] = value;
};

const useFormContext = (inputProps, defaultValue) => {
  var _a;
  const name = inputProps === null || inputProps === void 0 ? void 0 : inputProps.name;
  const [value, setInnerValue] = useState(cloneDeep(defaultValue));
  const formContext = useContext(FormContext);
  const [isTouched, setTouched] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const {
    register,
    resetForm,
    setValue: setFormValue,
    formValidations,
    formDefaultMessages,
    formState,
    unregister,
    viewMode: viewModeContext,
    forceDisabledStyleForViewMode,
    getValues,
    fieldsStateRef
  } = formContext;
  const disabledField = !(inputProps === null || inputProps === void 0 ? void 0 : inputProps.mountFieldWhileDisabled) && (inputProps === null || inputProps === void 0 ? void 0 : inputProps.disabled);
  const viewMode = viewModeContext || disabledField;
  const readOnly = (inputProps === null || inputProps === void 0 ? void 0 : inputProps.readOnly) || viewModeContext;
  const errorMessage = name && ((_a = get(formState === null || formState === void 0 ? void 0 : formState.errors, name)) === null || _a === void 0 ? void 0 : _a.message);
  const validations = useMemo(() => {
    let fieldValidations;
    if (name && !readOnly && formValidations && name in formValidations) {
      fieldValidations = formValidations[name];
    }
    return Object.assign(Object.assign({}, fieldValidations), inputProps === null || inputProps === void 0 ? void 0 : inputProps.validations);
  }, [formValidations, inputProps === null || inputProps === void 0 ? void 0 : inputProps.validations, name, readOnly]);
  const defaultMessages = useMemo(() => Object.assign(Object.assign({}, formDefaultMessages), inputProps === null || inputProps === void 0 ? void 0 : inputProps.defaultMessages), [formDefaultMessages, inputProps === null || inputProps === void 0 ? void 0 : inputProps.defaultMessages]);
  const unregisterField = useCallback(() => {
    if (name && unregister) {
      unregister(name);
      if (fieldsStateRef) {
        delete fieldsStateRef.current[name];
      }
    }
  }, [fieldsStateRef, name, unregister]);
  useEffect(() => {
    if (viewMode) {
      unregisterField();
    }
  }, [unregisterField, viewMode]);
  useEffect(() => {
    return unregisterField;
  }, [unregisterField]);
  const valueRef = useRef(value);
  valueRef.current = value;
  const setValue = useCallback(newValue => {
    let parsedValue = newValue;
    if (isValueFunction(parsedValue)) {
      parsedValue = parsedValue(valueRef.current);
    }
    setInnerValue(parsedValue);
    if (name && !disabledField && setFormValue) {
      setFormValue(name, parsedValue, {
        shouldValidate: Boolean(errorMessage)
      });
    }
  }, [name, setFormValue, errorMessage, disabledField]);
  // should not depend on value to trigger the register. only when validation or viewMode is changed
  useEffect(() => {
    if (name && !viewMode && register) {
      register(name, Object.assign(Object.assign({}, validationMap(validations, (inputProps === null || inputProps === void 0 ? void 0 : inputProps.required) || false, defaultMessages)), {
        value: valueRef.current
      }));
      const currentValue = getValues && getValues(name);
      if (valueRef.current !== currentValue) {
        setValue(valueRef.current);
      }
    }
  }, [name, viewMode, register, validations, inputProps === null || inputProps === void 0 ? void 0 : inputProps.required, defaultMessages, defaultValue, getValues, value, setValue]);
  const parsedResetForm = useMemo(() => name && Array.isArray(resetForm) && resetForm.includes(name) || resetForm === true, [resetForm, name]);
  useEffect(() => {
    if (resetForm) {
      setTouched(false);
    }
  }, [resetForm]);
  useEffect(() => setIsDirty(!isEqual(value, defaultValue)), [value, defaultValue]);
  useEffect(() => {
    if (parsedResetForm) {
      if (typeof (inputProps === null || inputProps === void 0 ? void 0 : inputProps.value) === "undefined" && defaultValue !== value) {
        setValue(cloneDeep(defaultValue));
      }
      if (inputProps === null || inputProps === void 0 ? void 0 : inputProps.onReset) {
        inputProps.onReset();
      }
    }
  }, [parsedResetForm, value, defaultValue, inputProps, setValue]);
  useEffect(() => {
    if (name && fieldsStateRef) {
      fieldsStateRef.current[name] = {
        isDirty,
        isTouched
      };
    }
  }, [isTouched, isDirty, name, fieldsStateRef]);
  const fieldState = useMemo(() => name ? {
    isDirty,
    isTouched
  } : undefined, [isDirty, isTouched, name]);
  const parsedForceDisabledStyleForViewMode = typeof (inputProps === null || inputProps === void 0 ? void 0 : inputProps.forceDisabledStyleForViewMode) === "boolean" ? inputProps.forceDisabledStyleForViewMode : forceDisabledStyleForViewMode;
  return Object.assign(Object.assign({}, formContext), {
    resetForm: parsedResetForm,
    resetFormFields: Array.isArray(resetForm) ? resetForm : [],
    setValue,
    setFormValue,
    formState,
    errorMessage: errorMessage,
    error: Boolean(errorMessage),
    value,
    readOnly,
    forceDisabledStyleForViewMode: parsedForceDisabledStyleForViewMode,
    fieldState,
    setTouched,
    defaultMessages
  });
};
const isValueFunction = value => Boolean(value && typeof value === "function");

const useStyles$4 = makeStyles(theme => {
  var _a;
  return {
    root: Object.assign({}, (_a = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _a === void 0 ? void 0 : _a.form)
  };
});

const Form = forwardRef((_a, formRef) => {
  var _b;
  var {
      children,
      onSubmit: onSubmitCallback,
      onError = noop,
      viewMode = false,
      validations,
      defaultMessages,
      formApi,
      forceDisabledStyleForViewMode = false
    } = _a,
    rest = __rest(_a, ["children", "onSubmit", "onError", "viewMode", "validations", "defaultMessages", "formApi", "forceDisabledStyleForViewMode"]);
  const classes = useStyles$4();
  const methods = useForm();
  const bParentForm = (_b = useFormContext()) === null || _b === void 0 ? void 0 : _b.api;
  const [resetForm, setResetForm] = useState(false);
  const fieldsStateRef = useRef({});
  const resetFormMethod = useCallback(function () {
    let fields = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    setResetForm(fields.length ? fields : true);
  }, []);
  const submitForm = useCallback(() => {
    if (internalFormRef.current) {
      internalFormRef.current.dispatchEvent(new Event("submit", {
        cancelable: true,
        bubbles: true
      }));
    }
  }, []);
  const isDirty = useCallback(() => {
    for (const fieldName in fieldsStateRef.current) {
      if (fieldsStateRef.current[fieldName].isDirty) {
        return true;
      }
    }
    return false;
  }, []);
  const isTouched = useCallback(() => {
    for (const fieldName in fieldsStateRef.current) {
      if (fieldsStateRef.current[fieldName].isTouched) {
        return true;
      }
    }
    return false;
  }, []);
  const api = useMemo(() => {
    const {
      clearErrors,
      getValues,
      trigger
    } = methods;
    return {
      clearErrors,
      getValues,
      trigger,
      resetForm: resetFormMethod,
      submitForm,
      isDirty,
      isTouched
    };
  }, [methods, resetFormMethod, submitForm, isDirty, isTouched]);
  if (formApi) {
    formApi.current = api;
  }
  const onSubmit = useCallback((data, event) => onSubmitCallback(data, methods.setError, event), [onSubmitCallback, methods, bParentForm]);
  useEffect(() => {
    if (resetForm) {
      setResetForm(false);
    }
  }, [resetForm]);
  const formValidations = useMemo(() => validations || {}, [validations]);
  const formDefaultMessages = useMemo(() => defaultMessages || {}, [defaultMessages]);
  const context = Object.assign(Object.assign({}, methods), {
    formValidations,
    formDefaultMessages,
    resetForm,
    api,
    viewMode,
    forceDisabledStyleForViewMode,
    fieldsStateRef
  });
  const handleSubmit = useCallback(event => {
    if (bParentForm) {
      event.stopPropagation();
    }
    methods.handleSubmit(viewMode ? noop : onSubmit, viewMode ? noop : onError)(event);
  }, [methods, onSubmit, viewMode, bParentForm, onError]);
  const internalFormRef = useRef(null);
  const defaultFormRefCallback = useCallback(ref => {
    if (formRef) {
      if (typeof formRef === "function") {
        formRef(ref);
      } else {
        formRef.current = ref;
      }
    }
    internalFormRef.current = ref;
  }, [formRef]);
  return React__default.createElement(FormContext.Provider, {
    value: context
  }, React__default.createElement("form", Object.assign({}, rest, {
    ref: defaultFormRefCallback,
    noValidate: true,
    onSubmit: handleSubmit,
    className: classes.root
  }), children));
});

const CheckboxIcon = props => React.createElement("svg", Object.assign({
  xmlns: "http://www.w3.org/2000/svg",
  width: "13",
  height: "13",
  viewBox: "0 0 13 13"
}, props), React.createElement("g", {
  "data-name": "Ret\\xE2ngulo 235",
  fill: "#fff",
  stroke: "#cbcbcb"
}, React.createElement("rect", {
  width: 13,
  height: 13,
  rx: 2,
  stroke: "none"
}), React.createElement("rect", {
  x: 0.5,
  y: 0.5,
  width: 12,
  height: 12,
  rx: 1.5,
  fill: "none"
})));

const CheckedCheckboxIcon = props => React.createElement("svg", Object.assign({
  xmlns: "http://www.w3.org/2000/svg",
  width: "13",
  height: "13",
  viewBox: "0 0 13 13"
}, props), React.createElement("g", {
  "data-name": "Grupo 29292",
  transform: "translate(-602.5 -130.5)"
}, React.createElement("rect", {
  "data-name": "Ret\\xE2ngulo 235",
  width: 13,
  height: 13,
  rx: 2,
  transform: "translate(602.5 130.5)",
  fill: "#24a58e"
}), React.createElement("path", {
  "data-name": "Caminho 126936",
  d: "m612.758 133.356-5.168 5.167-2.349-2.348-1.06 1.06 3.409 3.409 6.228-6.227Z",
  fill: "#fff"
})));

const DisabledCheckboxIcon = props => React.createElement("svg", Object.assign({
  xmlns: "http://www.w3.org/2000/svg",
  width: "13",
  height: "13",
  viewBox: "0 0 13 13"
}, props), React.createElement("rect", {
  width: 13,
  height: 13,
  rx: 2,
  fill: "#f5f5f5"
}));

const DisabledCheckedCheckboxIcon = props => React.createElement("svg", Object.assign({
  xmlns: "http://www.w3.org/2000/svg",
  width: "13",
  height: "13",
  viewBox: "0 0 13 13"
}, props), React.createElement("g", {
  transform: "translate(-710 -238.771)"
}, React.createElement("rect", {
  "data-name": "Ret\\xE2ngulo 10063",
  width: 13,
  height: 13,
  rx: 2,
  transform: "translate(710 238.771)",
  fill: "#f5f5f5"
}), React.createElement("path", {
  "data-name": "Caminho 126993",
  d: "m720.259 241.627-5.168 5.167-2.349-2.348-1.06 1.06 3.409 3.409 6.228-6.227Z",
  fill: "#cbcbcb"
})));

const IdGenerator = () => (Date.now() * Math.random()).toString().replace(".", "0");

const borderColor = "#CBCBCB";
const offColor = "#D8D8D8";
const lightColor = "#FFFFFF";
const labelColor = "#000000";
const errorColor = "#F84833";
const disabledColor = "#E5E5E5";
function getBreakpoints(theme) {
  return {
    desktop: theme.breakpoints.up("md"),
    mobile: theme.breakpoints.down("sm")
  };
}

const getCss = (theme, path, component) => Object.assign(Object.assign({}, get(theme, `forms.${path}`, undefined)), get(theme, `forms.${component}.${path}`, undefined));

const useStyles$3 = makeStyles(theme => {
  const breakpoints = getBreakpoints(theme);
  return {
    formControl: Object.assign({
      width: "100%"
    }, getCss(theme, "formControl", "inputText")),
    label: Object.assign(Object.assign({
      fontSize: "14px",
      position: "static",
      transform: "none",
      color: labelColor,
      "& + $input": {
        marginTop: "6px"
      }
    }, getCss(theme, "label.root", "inputText")), {
      "&$labelFocused": Object.assign({
        color: labelColor
      }, getCss(theme, "label.focused", "inputText")),
      "&$labelDisabled": Object.assign({}, getCss(theme, "label.disabled", "inputText")),
      "&$errorLabel": Object.assign({
        color: labelColor,
        "& $asterisk": {
          color: errorColor
        }
      }, getCss(theme, "label.error", "inputText")),
      "&$labelShrink": Object.assign({}, getCss(theme, "label.shrink", "inputText")),
      "&:hover": Object.assign({}, getCss(theme, "label.hover", "inputText"))
    }),
    labelDisabled: {},
    labelFocused: {},
    input: Object.assign(Object.assign({
      padding: 0,
      borderRadius: "4px",
      border: `1px solid ${borderColor}`
    }, getCss(theme, "input.root", "inputText")), {
      "& input": Object.assign({
        height: "30px",
        padding: "0 14px",
        fontSize: "14px",
        boxSizing: "inherit",
        "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
          WebkitBoxShadow: "0 0 0px 1000px #ffffff inset !important",
          borderRadius: "4px"
        },
        [breakpoints.mobile]: {
          height: "41px"
        }
      }, getCss(theme, "input.nativeInput", "inputText")),
      "& textarea": Object.assign({
        minHeight: "30px",
        fontSize: "14px",
        boxSizing: "inherit",
        padding: "5px 14px",
        "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
          WebkitBoxShadow: "0 0 0px 1000px #ffffff inset !important"
        }
      }, getCss(theme, "input.textarea", "inputText")),
      "&$focused": Object.assign({}, getCss(theme, "input.focused", "inputText")),
      "&$disabled": Object.assign({
        backgroundColor: disabledColor,
        borderColor: disabledColor
      }, getCss(theme, "input.disabled", "inputText")),
      "&$errorInput": Object.assign({
        color: labelColor,
        borderColor: errorColor
      }, getCss(theme, "input.error", "inputText")),
      "&:hover": Object.assign({}, getCss(theme, "input.hover", "inputText")),
      "&$underline:before": Object.assign({}, getCss(theme, "input.underline.root", "inputText")),
      "&$underline:hover:not($disabled):before": Object.assign({}, getCss(theme, "input.underline.hover", "inputText")),
      "&$underline:after": Object.assign({}, getCss(theme, "input.underline.active", "inputText"))
    }),
    focused: {},
    disabled: {},
    errorInput: {},
    errorLabel: {},
    asterisk: Object.assign({}, getCss(theme, "label.asterisk", "inputText")),
    required: Object.assign({}, getCss(theme, "label.required", "inputText")),
    readonlyInput: Object.assign({
      fontSize: "14px",
      minHeight: "14px",
      height: "auto",
      fontWeight: "bold",
      borderRadius: 0,
      border: `0px solid ${borderColor}`,
      paddingLeft: "0px",
      marginTop: "6px",
      lineHeight: "14px"
    }, getCss(theme, "input.readOnly", "inputText")),
    readonlyLabel: Object.assign({
      fontSize: "12px",
      "& + $input": {
        marginTop: "6px"
      }
    }, getCss(theme, "label.readOnly", "inputText")),
    formHelperText: Object.assign({
      color: errorColor,
      fontSize: "11px",
      lineHeight: "11px",
      marginLeft: "15px"
    }, getCss(theme, "input.helperText", "inputText")),
    underline: {},
    labelShrink: {},
    viewModeColor: Object.assign({
      width: "14px",
      height: "14px",
      borderRadius: "4px",
      marginLeft: "5px",
      display: "inline-block",
      verticalAlign: "bottom"
    }, getCss(theme, "input.viewModeColor", "inputText"))
  };
});

const MaskInput = _a => {
  var {
      onAccept,
      inputRef,
      onChange: parentOnChange,
      value
    } = _a,
    inputProps = __rest(_a, ["onAccept", "inputRef", "onChange", "value"]);
  const onValueChange = useCallback(function () {
    if (parentOnChange) {
      let newValue = arguments.length <= 0 ? undefined : arguments[0];
      if (onAccept) {
        newValue = onAccept(...arguments);
      }
      parentOnChange({
        target: {
          value: newValue
        }
      });
    }
  }, [onAccept, parentOnChange]);
  return React__default.createElement(IMaskInput, Object.assign({
    inputRef: inputRef,
    value: (typeof value === "undefined" ? "" : value).toString(),
    unmask: true,
    onAccept: onValueChange
  }, inputProps));
};

const InputComponent = forwardRef((props, ref) => {
  let component;
  const classes = useStyles$3();
  const parsedDefaultValue = useMemo(() => typeof props.value === "undefined" ? props.defaultValue || "" : props.value, [props]);
  const {
    setValue = noop,
    errorMessage,
    value: formValue,
    error,
    readOnly,
    forceDisabledStyleForViewMode,
    setTouched
  } = useFormContext(props, parsedDefaultValue);
  const defaultId = useMemo(() => IdGenerator(), []);
  const {
      name,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultValue = "",
      value,
      required = false,
      disabled = false,
      fullWidth,
      label,
      disableUnderline = true,
      shrink: inputShrink,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      validations = {},
      onChange,
      id = defaultId,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultMessages,
      type,
      maskProps,
      onFocus,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mountFieldWhileDisabled,
      // do not inject in dom
      multiline
    } = props,
    rest = __rest(props, ["name", "defaultValue", "value", "required", "disabled", "fullWidth", "label", "disableUnderline", "shrink", "validations", "onChange", "id", "defaultMessages", "type", "maskProps", "onFocus", "mountFieldWhileDisabled", "multiline"]);
  useEffect(() => {
    if (typeof value !== "undefined") {
      setValue(value);
    }
  }, [setValue, value]);
  const handleChange = useCallback(e => {
    setValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  }, [onChange, setValue]);
  const handleOnFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  }, [setTouched, onFocus]);
  let errorComponent;
  if (errorMessage) {
    errorComponent = React__default.createElement(FormHelperText, {
      className: classes.formHelperText
    }, errorMessage);
  }
  let input;
  if (readOnly && !forceDisabledStyleForViewMode && type !== "hidden") {
    const parsedFormValue = multiline ? (formValue || "").replace(/(?:\r\n|\r|\n)/g, "<br>") : formValue;
    const includesNewLines = typeof parsedFormValue === "string" && parsedFormValue.includes("<br>");
    let content;
    if (!includesNewLines) {
      content = formValue ? React__default.createElement(React__default.Fragment, null, !includesNewLines && parsedFormValue, type === "color" && React__default.createElement("div", {
        className: classes.viewModeColor,
        style: {
          backgroundColor: formValue
        }
      })) : "-";
    }
    input = React__default.createElement("span", {
      className: classes.readonlyInput,
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML: includesNewLines ? {
        __html: parsedFormValue
      } : undefined
    }, content);
  } else {
    const inputMaskProps = {};
    if (maskProps) {
      inputMaskProps.inputComponent = MaskInput;
      inputMaskProps.inputProps = merge({}, rest.inputProps, maskProps);
    }
    input = React__default.createElement(React__default.Fragment, null, React__default.createElement(Input, Object.assign({}, rest, inputMaskProps, {
      placeholder: rest === null || rest === void 0 ? void 0 : rest.placeholder,
      value: readOnly || disabled ? formValue || "-" : formValue,
      type: (readOnly || disabled) && !formValue && type !== "hidden" ? "text" : type,
      name: name,
      id: id,
      onChange: handleChange,
      disableUnderline: disableUnderline,
      error: error,
      disabled: disabled,
      required: required,
      readOnly: readOnly,
      onFocus: handleOnFocus,
      ref: ref,
      multiline: multiline,
      classes: type === "hidden" ? {} : {
        root: clsx(classes.input, {
          [classes.disabled]: readOnly && forceDisabledStyleForViewMode
        }),
        disabled: classes.disabled,
        focused: classes.focused,
        error: classes.errorInput,
        underline: classes.underline
      }
    })), errorComponent);
  }
  if (label && type !== "hidden") {
    component = React__default.createElement(FormControl, {
      disabled: disabled,
      fullWidth: fullWidth,
      className: classes.formControl
    }, React__default.createElement(InputLabel, {
      shrink: "shrink" in props ? inputShrink : true,
      required: required,
      htmlFor: id,
      error: error,
      focused: readOnly ? false : undefined,
      classes: {
        root: clsx(classes.label, {
          [classes.readonlyLabel]: readOnly && !forceDisabledStyleForViewMode
        }),
        required: classes.required,
        asterisk: classes.asterisk,
        focused: classes.labelFocused,
        disabled: classes.labelDisabled,
        error: classes.errorLabel,
        shrink: classes.labelShrink
      }
    }, React__default.createElement("span", null, label)), input);
  } else {
    component = input;
  }
  return component;
});

const useStyles$2 = makeStyles(theme => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
  const mobileBreakpoint = getBreakpoints(theme).mobile;
  return {
    formControl: Object.assign({
      display: "flex"
    }, getCss(theme, "formControl", "checkbox")),
    inline: Object.assign(Object.assign({
      display: "inline-block"
    }, (_c = (_b = (_a = theme.forms) === null || _a === void 0 ? void 0 : _a.checkbox) === null || _b === void 0 ? void 0 : _b.inline) === null || _c === void 0 ? void 0 : _c.root), {
      "& $controlLabel": Object.assign({
        marginTop: "17px"
      }, (_f = (_e = (_d = theme.forms) === null || _d === void 0 ? void 0 : _d.checkbox) === null || _e === void 0 ? void 0 : _e.inline) === null || _f === void 0 ? void 0 : _f.controlLabel)
    }),
    controlLabel: Object.assign({
      marginTop: 0,
      alignItems: "flex-start",
      [mobileBreakpoint]: {
        marginTop: "5px"
      }
    }, (_h = (_g = theme.forms) === null || _g === void 0 ? void 0 : _g.checkbox) === null || _h === void 0 ? void 0 : _h.controlLabel),
    label: Object.assign(Object.assign({
      fontSize: "12px",
      color: labelColor,
      userSelect: "none",
      marginTop: "10px"
    }, getCss(theme, "label.root", "checkbox")), {
      "&$labelDisabled": Object.assign({}, getCss(theme, "label.disabled", "checkbox")),
      "&:focus": Object.assign({}, getCss(theme, "label.focused", "checkbox")),
      "&:hover": Object.assign({}, getCss(theme, "label.hover", "checkbox"))
    }),
    labelDisabled: {},
    input: Object.assign(Object.assign({}, getCss(theme, "input.root", "checkbox")), {
      "&$disabled": Object.assign({}, getCss(theme, "input.disabled", "checkbox")),
      "&:focus": Object.assign({}, getCss(theme, "input.focused", "checkbox")),
      "&:hover": Object.assign({}, getCss(theme, "input.hover", "checkbox")),
      "&$checked": Object.assign({
        color: theme.palette.primary.main
      }, (_k = (_j = theme.forms) === null || _j === void 0 ? void 0 : _j.checkbox) === null || _k === void 0 ? void 0 : _k.checked)
    }),
    checked: {},
    icon: Object.assign({
      margin: "3.5px"
    }, (_m = (_l = theme.forms) === null || _l === void 0 ? void 0 : _l.checkbox) === null || _m === void 0 ? void 0 : _m.icon),
    checkedIcon: Object.assign({
      margin: "3.5px",
      "& rect": {
        fill: theme.palette.secondary.main
      }
    }, (_p = (_o = theme.forms) === null || _o === void 0 ? void 0 : _o.checkbox) === null || _p === void 0 ? void 0 : _p.checkedIcon),
    disabled: {},
    focused: {},
    required: Object.assign({}, getCss(theme, "label.required", "checkbox")),
    asterisk: Object.assign({}, getCss(theme, "label.asterisk", "checkbox")),
    readonlyLabel: Object.assign({
      fontSize: "12px",
      color: labelColor,
      marginTop: 0
    }, getCss(theme, "label.readOnly", "checkbox")),
    readonlyInput: Object.assign({}, getCss(theme, "input.readOnly", "checkbox")),
    errorLabel: Object.assign({
      color: labelColor,
      "& $asterisk": {
        color: errorColor
      }
    }, getCss(theme, "label.error", "checkbox")),
    errorInput: Object.assign({
      "& $icon g": {
        stroke: errorColor
      }
    }, getCss(theme, "input.error", "checkbox")),
    formHelperText: Object.assign({
      color: errorColor,
      fontSize: "11px",
      lineHeight: "15px",
      marginLeft: "28px"
    }, getCss(theme, "input.helperText", "checkbox"))
  };
});

const Checkbox = props => {
  const parsedValue = props.value || true;
  const defaultValue = useMemo(() => {
    if (typeof props.checked === "undefined") {
      return props.defaultChecked ? parsedValue : false;
    }
    return props.checked ? parsedValue : false;
  }, [props.defaultChecked, props.checked, parsedValue]);
  const parsedProps = useMemo(() => {
    const otherProps = __rest(props, ["value"]);
    return Object.assign(Object.assign({}, otherProps), {
      value: otherProps.checked
    });
  }, [props]);
  const {
    setValue = noop,
    errorMessage,
    value: formValue,
    error,
    readOnly,
    resetForm,
    setTouched
  } = useFormContext(parsedProps, defaultValue);
  const defaultId = useMemo(() => IdGenerator(), []);
  const {
      label,
      disabled,
      defaultChecked = false,
      id = defaultId,
      required = false,
      onChange,
      name,
      validations = {},
      defaultMessages,
      value,
      checked,
      defaultIcon: DefaultIcon = CheckboxIcon,
      defaultDisabledIcon: DefaultDisabledIcon = DisabledCheckboxIcon,
      defaultCheckedIcon: DefaultCheckedIcon = CheckedCheckboxIcon,
      defaultDisabledCheckedIcon: DefaultDisabledCheckedIcon = DisabledCheckedCheckboxIcon,
      onReset,
      inline,
      onFocus,
      mountFieldWhileDisabled
    } = props,
    // do not inject in dom
    rest = __rest(props, ["label", "disabled", "defaultChecked", "id", "required", "onChange", "name", "validations", "defaultMessages", "value", "checked", "defaultIcon", "defaultDisabledIcon", "defaultCheckedIcon", "defaultDisabledCheckedIcon", "onReset", "inline", "onFocus", "mountFieldWhileDisabled"]);
  let component;
  const classes = useStyles$2();
  useEffect(() => {
    if (typeof checked !== "undefined") {
      setValue(checked ? parsedValue : false);
    }
  }, [checked, setValue, parsedValue]);
  const handleChange = useCallback((e, changeChecked) => {
    if (typeof checked === "undefined") {
      setValue(changeChecked ? parsedValue : false);
    }
    if (onChange) {
      onChange(e, changeChecked);
    }
  }, [onChange, setValue, checked, parsedValue]);
  useEffect(() => {
    if (resetForm && onReset) {
      onReset();
    }
  }, [resetForm, onReset, checked]);
  const handleOnFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  }, [onFocus, setTouched]);
  if (readOnly && !disabled) {
    return React__default.createElement(InputComponent, {
      readOnly: readOnly,
      name: "readOnlyEmpty",
      value: formValue ? "Sim" : "Não",
      label: label || "",
      required: required,
      disabled: disabled
    });
  }
  const checkBoxMaterial = React__default.createElement(CheckboxMaterial, Object.assign({
    id: id,
    name: name,
    disabled: disabled,
    checked: Boolean(formValue),
    onChange: handleChange,
    value: value || true,
    disableRipple: readOnly,
    disableFocusRipple: readOnly,
    icon: disabled ? React__default.createElement(DefaultDisabledIcon, {
      className: classes.icon
    }) : React__default.createElement(DefaultIcon, {
      className: classes.icon
    }),
    checkedIcon: disabled ? React__default.createElement(DefaultDisabledCheckedIcon, {
      className: classes.checkedIcon
    }) : React__default.createElement(DefaultCheckedIcon, {
      className: classes.checkedIcon
    }),
    color: "primary",
    onFocus: handleOnFocus,
    classes: {
      root: clsx(classes.input, {
        [classes.errorInput]: error
      }),
      checked: classes.checked,
      disabled: classes.disabled
    }
  }, rest));
  if (label) {
    const labelComponent = [React__default.createElement("span", {
      key: `${id}-label`
    }, label)];
    if (required) {
      labelComponent.push(React__default.createElement("span", {
        key: `${id}-label-required`,
        className: classes.asterisk
      }, "*"));
    }
    component = React__default.createElement(FormControl, {
      className: clsx(classes.formControl, {
        [classes.inline]: inline
      })
    }, React__default.createElement(FormControlLabel, {
      label: labelComponent,
      classes: {
        root: classes.controlLabel,
        label: clsx(classes.label, {
          [classes.required]: required,
          [classes.readonlyLabel]: readOnly,
          [classes.errorLabel]: error
        })
      },
      control: checkBoxMaterial
    }), React__default.createElement(FormHelperText, {
      className: classes.formHelperText
    }, errorMessage));
  } else {
    component = checkBoxMaterial;
  }
  return component;
};

const InputNumber$1 = forwardRef((props, ref) => React__default.createElement(InputComponent, Object.assign({}, props, {
  ref: ref,
  type: "number"
})));

const InputNumber = forwardRef((props, ref) => React__default.createElement(InputComponent, Object.assign({}, props, {
  ref: ref,
  type: "password"
})));

const InputEmail = forwardRef((_a, ref) => {
  var {
      validations: inputValidations,
      defaultMessages: inputDefaultMessages
    } = _a,
    props = __rest(_a, ["validations", "defaultMessages"]);
  const validations = useMemo(() => Object.assign(Object.assign({}, inputValidations), {
    pattern:
    // eslint-disable-next-line no-control-regex
    /^(?:[a-z0-9!#$%&'*+/=?^_`{|}­~¨-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~¨-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/
  }), [inputValidations]);
  const defaultMessages = useMemo(() => Object.assign({
    pattern: "Introduza um email válido"
  }, inputDefaultMessages), [inputDefaultMessages]);
  return React__default.createElement(InputComponent, Object.assign({}, props, {
    ref: ref,
    validations: validations,
    defaultMessages: defaultMessages,
    type: "email"
  }));
});

const Textarea = forwardRef((_a, ref) => {
  var {
      minRows = 3
    } = _a,
    props = __rest(_a, ["minRows"]);
  return React__default.createElement(InputComponent, Object.assign({}, props, {
    ref: ref,
    type: "text",
    multiline: true,
    minRows: minRows
  }));
});

function CloseIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 11.7 11.7",
    width: "11.7",
    height: "11.7"
  }, props), React.createElement("path", {
    d: "M1.7 10.7l4.1-4.1 4.2 4.2c.2.2.5.2.7 0 .1-.1.1-.2.1-.4s-.1-.3-.1-.4L6.6 5.8l4.1-4.2c.1-.1.1-.2.1-.4 0-.1-.1-.3-.1-.4-.2-.2-.5-.2-.7 0L5.8 5.1 1.7 1C1.5.8 1.2.8 1 1c-.2.2-.2.5 0 .7l4.2 4.1L1 10c-.1.1-.1.2-.1.4s.1.3.1.4c.2.1.5.1.7-.1z",
    fill: "#404040"
  }));
}

function FaileIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "10.282",
    height: "14",
    viewBox: "0 0 10.282 14"
  }, props), React.createElement("path", {
    id: "file3",
    d: "M27.173,8.2,24.062,5.093A.307.307,0,0,0,23.844,5H17.933A.936.936,0,0,0,17,5.933v5.289a.328.328,0,0,0,.078.2l1.6,1.867a.312.312,0,0,0,.467,0L20.516,11.7l1.369,1.587a.312.312,0,0,0,.467,0L23.72,11.7l1.369,1.587a.3.3,0,0,0,.467,0l1.6-1.867a.328.328,0,0,0,.078-.2v-2.8A.2.2,0,0,0,27.173,8.2ZM24.156,6.058l2.053,2.053H24.467a.312.312,0,0,1-.311-.311Zm1.2,6.549L23.984,11.02a.3.3,0,0,0-.467,0l-1.384,1.587L20.764,11.02a.312.312,0,0,0-.467,0l-1.369,1.587-1.307-1.493V5.933a.312.312,0,0,1,.311-.311h5.6V7.8a.936.936,0,0,0,.933.933h2.178v2.38Zm1.711.187a.289.289,0,0,0-.342.093l-1.369,1.587-1.369-1.587a.312.312,0,0,0-.467,0l-1.384,1.587-1.369-1.587a.312.312,0,0,0-.467,0l-1.369,1.587L17.56,12.887a.312.312,0,0,0-.342-.093.3.3,0,0,0-.2.3v4.978a.936.936,0,0,0,.933.933h8.4a.936.936,0,0,0,.933-.933V13.089A.361.361,0,0,0,27.064,12.793Zm-.42,5.273a.312.312,0,0,1-.311.311h-8.4a.312.312,0,0,1-.311-.311V13.929l1.058,1.229a.312.312,0,0,0,.467,0l1.369-1.587,1.369,1.587a.312.312,0,0,0,.467,0l1.369-1.587,1.369,1.587a.312.312,0,0,0,.467,0l1.058-1.229v4.138Z",
    transform: "translate(-17 -5)",
    fill: "#d70000"
  }));
}

function FiledIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "10.267",
    height: "14",
    viewBox: "0 0 10.267 14"
  }, props), React.createElement("path", {
    id: "file",
    d: "M27.173,8.2,24.062,5.093A.307.307,0,0,0,23.844,5H17.933A.936.936,0,0,0,17,5.933V18.067a.936.936,0,0,0,.933.933h8.4a.936.936,0,0,0,.933-.933V8.422A.307.307,0,0,0,27.173,8.2ZM24.156,6.058l2.053,2.053H24.467a.312.312,0,0,1-.311-.311Zm2.178,12.32h-8.4a.312.312,0,0,1-.311-.311V5.933a.312.312,0,0,1,.311-.311h5.6V7.8a.936.936,0,0,0,.933.933h2.178v9.333A.312.312,0,0,1,26.333,18.378Z",
    transform: "translate(-17 -5)",
    fill: "#404147"
  }));
}

function SvgRetryUploadIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "10.487",
    height: "10.191",
    viewBox: "0 0 10.487 10.191"
  }, props), React.createElement("defs", null, React.createElement("clipPath", {
    id: "RetryUploadIcon_svg__a"
  }, React.createElement("path", {
    "data-name": "Ret\\xE2ngulo 9602",
    fill: "none",
    d: "M0 0h10.487v10.191H0z"
  }))), React.createElement("g", {
    "data-name": "Grupo 26298",
    clipPath: "url(#RetryUploadIcon_svg__a)"
  }, React.createElement("path", {
    "data-name": "Caminho 126922",
    d: "M1.568 1.459A5.152 5.152 0 015.241 0a5.452 5.452 0 013.3 1.134c.1.08.2.166.292.253l.913-.51a.462.462 0 01.517.011.459.459 0 01.21.489l-.367 2.194a.483.483 0 01-.227.325.5.5 0 01-.421.045l-2.119-.79a.5.5 0 01-.34-.4.47.47 0 01.241-.471l.668-.377-.01-.01a4.266 4.266 0 00-6.916 3.2 4.2 4.2 0 004.2 4.134h.13a4.194 4.194 0 003.8-2.444q.063-.3.109-.7a.509.509 0 011.017.024 2.717 2.717 0 01-.059.544h.019c-.009.027-.024.053-.033.079a1.631 1.631 0 01-.2.476 5.146 5.146 0 01-4.646 2.991h-.143A5.191 5.191 0 01-.002 5.091a5.137 5.137 0 011.57-3.632",
    fill: "#404147"
  })));
}

const isBaseFile = file => file && file instanceof File;
const isFileWithId = file => file && "id" in file;
const isSelectOption = option => typeof option === "object" && option !== null && "id" in option;

const DropFilesContainer = _ref => {
  let {
    onDroppedFiles,
    fileUploaderButton,
    dropTranslations,
    classes
  } = _ref;
  const [{
    canDrop,
    isOver
  }, drop] = useDrop(() => ({
    accept: [NativeTypes.FILE],
    drop(_ref2) {
      let {
        files
      } = _ref2;
      onDroppedFiles(files);
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [onDroppedFiles]);
  const isActive = canDrop && isOver;
  return React__default.createElement(React__default.Fragment, null, React__default.createElement("div", {
    ref: drop,
    className: clsx(classes.dropFilesContainer, {
      [classes.dropFilesContainerOnHover]: isActive
    })
  }, React__default.createElement("span", {
    className: classes.dropFilesLabel
  }, isActive ? dropTranslations === null || dropTranslations === void 0 ? void 0 : dropTranslations.whitFiles : dropTranslations === null || dropTranslations === void 0 ? void 0 : dropTranslations.withoutFiles), fileUploaderButton));
};

const useFileUploaderStyles = makeStyles(theme => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7;
  const breakpoints = getBreakpoints(theme);
  return {
    button: Object.assign({
      height: "32px",
      textTransform: "none",
      color: "white",
      fontSize: "14px",
      fontWeight: "bold",
      padding: "0px 18px",
      borderRadius: "7px",
      backgroundColor: theme.palette.primary.main,
      [breakpoints.mobile]: {
        height: "41px"
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.main
      },
      "&:focus": {
        backgroundColor: theme.palette.primary.main
      },
      //This next class styles are unused because FileUploader only has 1 variant, but can be useful
      //if in the future it will need different variants
      //or a Button Component that extends from the MaterialUI Button is created
      "&.MuiButton-outlined": {
        backgroundColor: "transparent",
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: "transparent"
        },
        "&:focus": {
          backgroundColor: "transparent"
        }
      },
      "&.MuiButton-text": {
        backgroundColor: "transparent",
        borderColor: "transparent",
        color: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: "transparent"
        },
        "&:focus": {
          backgroundColor: "transparent"
        }
      }
    }, (_b = (_a = theme.forms) === null || _a === void 0 ? void 0 : _a.fileUploader) === null || _b === void 0 ? void 0 : _b.button),
    readOnlyButton: {
      display: "none"
    },
    deleteButton: Object.assign({
      padding: "8px"
    }, (_d = (_c = theme.forms) === null || _c === void 0 ? void 0 : _c.fileUploader) === null || _d === void 0 ? void 0 : _d.deleteButton),
    fileContainer: Object.assign({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      marginTop: "5px"
    }, (_f = (_e = theme.forms) === null || _e === void 0 ? void 0 : _e.fileUploader) === null || _f === void 0 ? void 0 : _f.fileContainer),
    filename: Object.assign({
      textOverflow: "ellipsis",
      overflowX: "hidden",
      whiteSpace: "nowrap",
      color: theme.palette.primary.main
    }, (_h = (_g = theme.forms) === null || _g === void 0 ? void 0 : _g.fileUploader) === null || _h === void 0 ? void 0 : _h.filename),
    filenameReadOnly: {
      width: "100%"
    },
    inputContainer: Object.assign({
      marginTop: "6px",
      marginBottom: "16px"
    }, (_k = (_j = theme.forms) === null || _j === void 0 ? void 0 : _j.fileUploader) === null || _k === void 0 ? void 0 : _k.inputContainer),
    formHelperText: Object.assign(Object.assign({
      color: errorColor,
      fontSize: "11px",
      lineHeight: "11px",
      marginLeft: "15px"
    }, getCss(theme, "input.helperText", "fileUploader")), (_m = (_l = theme.forms) === null || _l === void 0 ? void 0 : _l.fileUploader) === null || _m === void 0 ? void 0 : _m.helperText),
    formControl: Object.assign({
      width: "100%"
    }, getCss(theme, "formControl", "fileUploader")),
    dropFilesContainer: Object.assign({
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      textAlign: "center",
      width: "100%",
      marginTop: "6px",
      marginBottom: "16px",
      padding: "5px 10px 5px 10px",
      border: `2px dashed ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      backgroundColor: "transparent"
    }, (_q = (_p = (_o = theme.forms) === null || _o === void 0 ? void 0 : _o.fileUploader) === null || _p === void 0 ? void 0 : _p.dropFilesContainer) === null || _q === void 0 ? void 0 : _q.root),
    dropFilesContainerOnHover: Object.assign({
      backgroundColor: "#F3F4F5"
    }, (_t = (_s = (_r = theme.forms) === null || _r === void 0 ? void 0 : _r.fileUploader) === null || _s === void 0 ? void 0 : _s.dropFilesContainer) === null || _t === void 0 ? void 0 : _t.hover),
    label: Object.assign(Object.assign({
      color: labelColor,
      fontSize: "14px",
      transform: "none",
      position: "static"
    }, getCss(theme, "label.root", "fileUploader")), {
      "&$labelFocused": Object.assign({
        color: labelColor
      }, getCss(theme, "label.focused", "fileUploader")),
      "&$labelDisabled": Object.assign({}, getCss(theme, "label.disabled", "fileUploader")),
      "&$errorLabel": Object.assign({
        color: labelColor,
        "& $asterisk": {
          color: errorColor
        }
      }, getCss(theme, "label.error", "fileUploader")),
      "&$labelShrink": Object.assign({}, getCss(theme, "label.shrink", "fileUploader")),
      "&:hover": Object.assign({}, getCss(theme, "label.hover", "fileUploader"))
    }),
    asterisk: Object.assign({}, getCss(theme, "label.asterisk", "fileUploader")),
    required: Object.assign({}, getCss(theme, "label.required", "fileUploader")),
    readonlyLabel: Object.assign({
      fontSize: "12px"
    }, getCss(theme, "label.readOnly", "fileUploader")),
    labelDisabled: {},
    labelFocused: {},
    focused: {},
    errorLabel: {},
    disabled: {},
    circularLoadingContainer: {
      position: "relative",
      marginRight: "5px"
    },
    circularProcess: {
      display: "block"
    },
    circularPercentage: {
      position: "absolute",
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "8px"
    },
    endButtons: {
      flexGrow: 1
    },
    dropFilesLabel: Object.assign({}, (_v = (_u = theme.forms) === null || _u === void 0 ? void 0 : _u.fileUploader) === null || _v === void 0 ? void 0 : _v.dropFilesLabel),
    fileIcon: Object.assign({
      marginRight: "7px"
    }, (_y = (_x = (_w = theme.forms) === null || _w === void 0 ? void 0 : _w.fileUploader) === null || _x === void 0 ? void 0 : _x.icons) === null || _y === void 0 ? void 0 : _y.fileIcon),
    retryIcon: Object.assign({}, (_1 = (_0 = (_z = theme.forms) === null || _z === void 0 ? void 0 : _z.fileUploader) === null || _0 === void 0 ? void 0 : _0.icons) === null || _1 === void 0 ? void 0 : _1.retryIcon),
    failedIcon: Object.assign({
      marginRight: "7px"
    }, (_4 = (_3 = (_2 = theme.forms) === null || _2 === void 0 ? void 0 : _2.fileUploader) === null || _3 === void 0 ? void 0 : _3.icons) === null || _4 === void 0 ? void 0 : _4.failedIcon),
    deleteIcon: Object.assign({
      "& path": {
        fill: errorColor
      }
    }, (_7 = (_6 = (_5 = theme.forms) === null || _5 === void 0 ? void 0 : _5.fileUploader) === null || _6 === void 0 ? void 0 : _6.icons) === null || _7 === void 0 ? void 0 : _7.deleteIcon),
    labelShrink: {}
  };
});

/* eslint-disable no-param-reassign */
const defaultDropTranslations = {
  whitFiles: "Largue os ficheiros aqui",
  withoutFiles: "Arraste ou selecione ficheiros",
  whitFile: "Largue o ficheiro aqui",
  withoutFile: "Arraste ou selecione um ficheiro"
};
const FileUploaderWithRef = (props, ref) => {
  const defaultValue = useMemo(() => typeof props.value === "undefined" ? props.defaultFiles || [] : props.value, [props === null || props === void 0 ? void 0 : props.defaultFiles, props.value]);
  const {
    maxFileSize,
    maxFilesSize,
    fileTypes,
    extensionsTypes,
    maxNumberFiles,
    validations: customValidations,
    directUploadHandler,
    abortDirectUpload,
    icons: {
      deleteIcon: CustomDeleteIcon = CloseIcon,
      failedIcon: CustomFailedIcon = FaileIcon,
      fileIcon: CustomFileIcon = FiledIcon,
      retryIcon: CustomRetryIcon = SvgRetryUploadIcon
    } = {}
  } = props;
  const fileTypeExtensions = useMemo(() => {
    const extensions = [];
    for (const fileType of fileTypes || []) {
      const newExtension = mime.getExtension(fileType);
      if (newExtension) {
        extensions.push(newExtension);
      }
    }
    return extensions;
  }, [fileTypes]);
  const allowedExtensions = useMemo(() => {
    const extensions = [];
    for (const extensionsType of extensionsTypes || []) {
      if (!extensions.includes(extensionsType)) {
        extensions.push(extensionsType);
      }
    }
    return extensions;
  }, [extensionsTypes]);
  const parseDefaultMessageProps = useMemo(() => ({
    defaultMessages: props.defaultMessages,
    name: ""
  }), [props.defaultMessages]);
  const {
    defaultMessages: defaultMessages$1
  } = useFormContext(parseDefaultMessageProps);
  const maxFileSizeValidation = useCallback(files => {
    if (maxFileSize) {
      const limitSize = maxFileSize * 1000;
      for (const file of files || []) {
        if (limitSize < (file.size || 0)) {
          return getMessageWithArguments((defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.maxFileSize) || defaultMessages.maxFileSize, maxFileSize.toString());
        }
      }
    }
    return true;
  }, [maxFileSize, defaultMessages$1]);
  const maxFilesSizeValidation = useCallback(files => {
    if (maxFilesSize) {
      const limitSize = maxFilesSize * 1000;
      let size = 0;
      for (const file of files || []) {
        size += file.size || 0;
        if (limitSize < size) {
          return getMessageWithArguments((defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.maxFilesSize) || defaultMessages.maxFilesSize, maxFilesSize.toString());
        }
      }
    }
    return true;
  }, [maxFilesSize, defaultMessages$1]);
  const fileTypesValidation = useCallback(files => {
    if (fileTypes) {
      for (const file of files || []) {
        if (!isFileWithId(file) && !fileTypes.includes(file.type)) {
          return getMessageWithArguments((defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.fileTypes) || defaultMessages.fileTypes, fileTypeExtensions.join(", "));
        }
      }
    }
    return true;
  }, [fileTypes, fileTypeExtensions, defaultMessages$1]);
  const extensionTypesValidation = useCallback(files => {
    if (extensionsTypes === null || extensionsTypes === void 0 ? void 0 : extensionsTypes.length) {
      for (const file of files || []) {
        const fileNameArray = file.name.split(".");
        const fileExtension = fileNameArray.length > 1 ? fileNameArray.pop() : "";
        if (!extensionsTypes.includes(fileExtension.toLowerCase())) {
          return getMessageWithArguments((defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.fileTypes) || defaultMessages.fileTypes, allowedExtensions.join(", "));
        }
      }
    }
    return true;
  }, [extensionsTypes, allowedExtensions, defaultMessages$1]);
  const maxNumberFilesValidation = useCallback(files => {
    if (props.multiple && maxNumberFiles && (files && files.length || 0) > maxNumberFiles) {
      return getMessageWithArguments((defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.maxNumberFiles) || defaultMessages.maxNumberFiles, maxNumberFiles.toString());
    }
    return true;
  }, [maxNumberFiles, defaultMessages$1, props.multiple]);
  const validations = useMemo(() => {
    const parsedValidations = customValidations || {};
    let validate = parsedValidations.validate || {};
    if (typeof validate === "function") {
      validate = {
        customValidation: validate
      };
    }
    validate.maxFileSizeValidation = maxFileSizeValidation;
    validate.maxFilesSizeValidation = maxFilesSizeValidation;
    validate.fileTypesValidation = fileTypesValidation;
    validate.extensionTypesValidation = extensionTypesValidation;
    validate.maxNumberFilesValidation = maxNumberFilesValidation;
    return {
      validations: Object.assign(Object.assign({}, customValidations), {
        validate
      })
    };
  }, [customValidations, maxFileSizeValidation, maxFilesSizeValidation, fileTypesValidation, extensionTypesValidation, maxNumberFilesValidation]);
  const parsedProps = useMemo(() => Object.assign(Object.assign({}, validations), props), [validations, props]);
  const {
    setValue = noop,
    errorMessage,
    resetForm,
    value,
    error,
    readOnly,
    setTouched = noop
  } = useFormContext(parsedProps, defaultValue.length ? defaultValue : undefined);
  useEffect(() => {
    if (typeof props.value !== "undefined") {
      setValue(props.value);
    }
  }, [props.value, setValue]);
  const defaultId = useMemo(() => IdGenerator(), []);
  const {
    name,
    label,
    disabled = false,
    id = defaultId,
    onAddedFiles,
    required = false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    accept,
    fullWidth,
    multiple = false,
    renderFiles,
    renderButton,
    fileUploadLabel = "Submit file",
    enableDragAndDrop = false,
    dropTranslations,
    shrink: inputShrink,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mountFieldWhileDisabled,
    // ignore prop
    onReset,
    onChange
  } = props;
  const parsedDropTranslations = useMemo(() => merge({}, defaultDropTranslations, dropTranslations), [dropTranslations]);
  const processDirectUpload = useCallback(file => {
    if (directUploadHandler && typeof props.value === "undefined") {
      const internalFileId = getFileInternalId(name, file.name);
      setFilesUploaded(currentUploads => {
        currentUploads[internalFileId] = {
          progress: 0
        };
        return Object.assign({}, currentUploads);
      });
      const updateProgress = percentage => {
        setFilesUploaded(currentUploads => {
          if (internalFileId in currentUploads) {
            currentUploads[internalFileId].progress = percentage;
            return Object.assign({}, currentUploads);
          }
          return currentUploads;
        });
      };
      directUploadHandler(internalFileId, file, updateProgress).then(fileUploaded => {
        setValue(currentValue => {
          if (fileUploaded && currentValue) {
            const foundFileIndex = currentValue.findIndex(itemFile => itemFile.name === file.name);
            if (foundFileIndex > -1) {
              currentValue.splice(foundFileIndex, 1, {
                id: fileUploaded.id,
                name: file.name,
                url: URL.createObjectURL(file),
                params: fileUploaded.params
              });
              return [...currentValue];
            }
          } else if (currentValue) {
            setFilesUploaded(currentUploads => {
              if (internalFileId in currentUploads) {
                currentUploads[internalFileId] = {
                  progress: 0,
                  retry: true
                };
                return Object.assign({}, currentUploads);
              }
              return currentUploads;
            });
          }
          return currentValue;
        });
      });
    }
  }, [directUploadHandler, name, setValue, props.value]);
  const dragAndDropCallback = useCallback(files => {
    const newFilesAdded = [];
    const newFiles = value ? [...value] : [];
    if (multiple) {
      if (files) {
        for (let i = 0; i < files.length; i += 1) {
          const file = files[i];
          const foundFile = newFiles.find(itemFile => itemFile.name === file.name);
          if (!foundFile) {
            newFilesAdded.push(file);
            newFiles.push(file);
            processDirectUpload(file);
          }
        }
      }
      if (typeof props.value === "undefined") {
        setValue(newFiles.length ? newFiles : undefined);
      }
    } else {
      newFilesAdded.push(files[0]);
      newFiles.push(files[0]);
      if (typeof props.value === "undefined") {
        processDirectUpload(files[0]);
        setValue(newFiles);
      }
    }
    if (onAddedFiles && newFilesAdded.length) {
      onAddedFiles(newFilesAdded);
    }
    if (onChange) {
      onChange(newFiles);
    }
  }, [value, multiple, onAddedFiles, onChange, props.value, processDirectUpload, setValue]);
  const acceptTypes = useMemo(() => {
    const acceptType = allowedExtensions.concat(fileTypeExtensions);
    return acceptType === null || acceptType === void 0 ? void 0 : acceptType.map(extension => `.${extension}`).join(",");
  }, [allowedExtensions, fileTypeExtensions]);
  const classes = useFileUploaderStyles();
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => inputRef.current);
  const [filesUploaded, setFilesUploaded] = useState({});
  const processNewFiles = useCallback(files => {
    const newFilesAdded = [];
    let newFiles = value ? [...value] : [];
    if (files) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const foundFile = newFiles.find(itemFile => itemFile.name === file.name);
        if (!foundFile) {
          newFilesAdded.push(file);
          if (multiple) {
            newFiles.push(file);
          } else {
            newFiles = [file];
          }
          processDirectUpload(file);
        }
      }
    }
    if (typeof props.value === "undefined") {
      setValue(newFiles.length ? newFiles : undefined);
    } else if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (onAddedFiles && newFilesAdded.length) {
      onAddedFiles(newFilesAdded);
    }
    if (onChange) {
      onChange(newFiles);
    }
  }, [multiple, onAddedFiles, onChange, processDirectUpload, props.value, setValue, value]);
  const addFiles = useCallback(files => {
    if (inputRef.current) {
      processNewFiles(Array.isArray(files) ? files : [files]);
    }
  }, [processNewFiles]);
  const handleChange = useCallback(e => processNewFiles(e.target.files), [processNewFiles]);
  const clearInput = useCallback(filename => {
    if (value) {
      const indexToRemove = value.findIndex(_ref => {
        let {
          name: itemName
        } = _ref;
        return itemName === filename;
      });
      if (indexToRemove !== -1) {
        value.splice(indexToRemove, 1);
      }
      const fileInternalId = getFileInternalId(name, filename);
      if (typeof props.value === "undefined") {
        setValue(value.length ? [...value] : undefined);
        setFilesUploaded(currentFilesUploaded => {
          let result = currentFilesUploaded;
          if (fileInternalId in currentFilesUploaded) {
            delete currentFilesUploaded[fileInternalId];
            result = Object.assign({}, currentFilesUploaded);
          }
          return result;
        });
        if (abortDirectUpload) {
          abortDirectUpload(fileInternalId);
        }
      }
      if (props.onDeletedFile) {
        props.onDeletedFile(filename);
      }
    }
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.dispatchEvent(new Event("change", {
        bubbles: true
      }));
    }
  }, [value, name, props, setValue, abortDirectUpload]);
  useEffect(() => {
    if (resetForm && !isEqual(defaultValue, value)) {
      if (typeof props.value === "undefined") {
        setValue([...defaultValue]);
      } else if (onReset) {
        onReset();
      }
    }
  }, [resetForm, defaultValue, value, setValue, props.value, onReset]);
  const handleDelete = useCallback(filename => {
    setTouched(true);
    clearInput(filename);
  }, [clearInput, setTouched]);
  const onDelete = useCallback(e => handleDelete(e.currentTarget.dataset.fileName), [handleDelete]);
  const openDialog = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
      setTouched(true);
    }
  }, [inputRef, setTouched]);
  const inputComponent = React__default.createElement("input", {
    ref: inputRef,
    accept: acceptTypes,
    disabled: disabled,
    hidden: true,
    type: "file",
    id: id,
    name: name,
    onChange: handleChange,
    multiple: multiple,
    readOnly: readOnly
  });
  let baseComponent;
  const addFilesButton = renderButton ? renderButton(openDialog, disabled, addFiles) : React__default.createElement(PrimaryButton, {
    disabled: disabled,
    className: readOnly ? classes.readOnlyButton : "",
    onClick: openDialog
  }, fileUploadLabel);
  const handleRetryUpload = useCallback(filename => {
    const fileToRetry = (value || []).find(fileItem => fileItem.name === filename);
    if (fileToRetry && !isFileWithId(fileToRetry)) {
      processDirectUpload(fileToRetry);
    }
  }, [processDirectUpload, value]);
  const onRetryUpload = useCallback(e => handleRetryUpload(e.currentTarget.dataset.fileName), [handleRetryUpload]);
  const getUrlFile = useCallback(file => {
    if (isFileWithId(file)) {
      return file.url;
    }
    return URL.createObjectURL(file);
  }, []);
  const renderFilesAPI = useMemo(() => ({
    deleteFile: handleDelete,
    retryFile: handleRetryUpload,
    getUrlFile
  }), [getUrlFile, handleDelete, handleRetryUpload]);
  if (value && value.length) {
    if (renderFiles) {
      baseComponent = renderFiles(value, filesUploaded, renderFilesAPI);
    } else {
      baseComponent = value.map(file => {
        const deleteComponent = readOnly || disabled ? undefined : React__default.createElement(IconButton, {
          "data-file-name": file.name,
          className: classes.deleteButton,
          onClick: onDelete
        }, React__default.createElement(CustomDeleteIcon, {
          className: classes.deleteIcon
        }));
        const url = getUrlFile(file);
        let uploadProgressComponent;
        let retryComponent;
        let fileIconComponent;
        if (isFileWithId(file)) {
          fileIconComponent = React__default.createElement(CustomFileIcon, {
            className: classes.fileIcon
          });
        } else {
          const fileUploaded = filesUploaded === null || filesUploaded === void 0 ? void 0 : filesUploaded[getFileInternalId(name, file.name)];
          if (fileUploaded) {
            if (fileUploaded.retry) {
              retryComponent = React__default.createElement(IconButton, {
                "data-file-name": file.name,
                onClick: onRetryUpload
              }, React__default.createElement(CustomRetryIcon, {
                className: classes.retryIcon
              }));
              fileIconComponent = React__default.createElement(CustomFailedIcon, {
                className: classes.failedIcon
              });
            } else {
              uploadProgressComponent = React__default.createElement("div", {
                className: classes.circularLoadingContainer
              }, React__default.createElement(CircularProgress, {
                variant: "determinate",
                value: fileUploaded.progress,
                size: 30,
                className: classes.circularProcess
              }), React__default.createElement("div", {
                className: classes.circularPercentage
              }, React__default.createElement("span", null, fileUploaded.progress, "%")));
            }
          } else {
            fileIconComponent = React__default.createElement(CustomFileIcon, {
              className: classes.fileIcon
            });
          }
        }
        let endButtonsComponent;
        if (retryComponent || deleteComponent) {
          endButtonsComponent = React__default.createElement("div", {
            className: classes.endButtons
          }, retryComponent, deleteComponent);
        }
        return React__default.createElement("div", {
          key: file.name,
          className: classes.fileContainer
        }, fileIconComponent, uploadProgressComponent, React__default.createElement("a", {
          href: url,
          className: clsx(classes.filename, {
            [classes.filenameReadOnly]: readOnly
          }),
          title: file.name,
          download: file.name
        }, file.name), endButtonsComponent);
      });
    }
  } else if (readOnly || disabled) {
    return React__default.createElement(InputComponent, {
      name: "readOnlyEmpty",
      readOnly: readOnly,
      label: label,
      disabled: disabled
    });
  }
  let inputContainer;
  if (!readOnly && (!value || !value.length || multiple)) {
    if (enableDragAndDrop) {
      inputContainer = React__default.createElement(DndProvider, {
        backend: HTML5Backend
      }, React__default.createElement(DropFilesContainer, {
        onDroppedFiles: dragAndDropCallback,
        fileUploaderButton: addFilesButton,
        dropTranslations: parsedDropTranslations,
        classes: classes
      }));
    } else {
      inputContainer = React__default.createElement("div", {
        className: classes.inputContainer
      }, addFilesButton);
    }
  }
  const input = React__default.createElement(React__default.Fragment, null, readOnly ? undefined : inputComponent, inputContainer, baseComponent, React__default.createElement(FormHelperText, {
    className: classes.formHelperText
  }, errorMessage));
  if (label) {
    return React__default.createElement(FormControl, {
      disabled: disabled,
      fullWidth: fullWidth,
      className: classes.formControl
    }, React__default.createElement(InputLabel, {
      shrink: "shrink" in props ? inputShrink : true,
      required: required,
      htmlFor: id,
      error: error,
      focused: readOnly ? false : undefined,
      classes: {
        root: clsx(classes.label, {
          [classes.readonlyLabel]: readOnly
        }),
        required: classes.required,
        asterisk: classes.asterisk,
        focused: classes.labelFocused,
        disabled: classes.labelDisabled,
        error: classes.errorLabel,
        shrink: classes.labelShrink
      }
    }, React__default.createElement("span", null, label)), input);
  }
  return input;
};
const FileUploader = React__default.forwardRef(FileUploaderWithRef);
const getFileInternalId = (fieldName, fileName) => `${fieldName}${fileName}`;

function SelectDownArrowIcon(props) {
  return React.createElement("svg", Object.assign({
    xmlns: "http://www.w3.org/2000/svg",
    width: "13.527px",
    height: "7.302px",
    viewBox: "0 0 13.527 7.302"
  }, props), React.createElement("path", {
    d: "M.707.707l6.056 6.1 6.056-6.1",
    fill: "none",
    stroke: "#bfbfbf",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }));
}

const useSelectStyles = makeStyles(theme => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
  const breakpoints = getBreakpoints(theme);
  return {
    formControl: Object.assign({
      minWidth: "100px",
      width: "100%"
    }, getCss(theme, "formControl", "select")),
    label: Object.assign(Object.assign({
      transform: "none",
      position: "static",
      color: labelColor,
      fontSize: "14px",
      "& + $inputClasses": {
        marginTop: "6px"
      }
    }, getCss(theme, "label.root", "select")), {
      "&$labelDisabled": Object.assign({}, getCss(theme, "label.disabled", "select")),
      "&$labelFocused": Object.assign({
        color: labelColor
      }, getCss(theme, "label.focused", "select")),
      "&$errorLabel": Object.assign({
        color: labelColor,
        "& $asterisk": {
          color: errorColor
        }
      }, getCss(theme, "label.error", "select")),
      "&$labelShrink": Object.assign({}, getCss(theme, "label.shrink", "select")),
      "&:hover": Object.assign({}, getCss(theme, "label.hover", "select"))
    }),
    labelDisabled: {},
    labelFocused: {},
    input: Object.assign(Object.assign({
      height: "30px",
      "& span": {
        lineHeight: "30px",
        verticalAlign: "middle"
      },
      [breakpoints.mobile]: {
        height: "39px",
        "& span": {
          lineHeight: "39px",
          verticalAlign: "middle"
        }
      },
      fontSize: "14px",
      borderRadius: 4,
      border: `1px solid ${borderColor}`,
      "&[class*='MuiSelect-select']": {
        padding: "0px 30px 0px 14px"
      }
    }, getCss(theme, "input.root", "select")), {
      "&$disabled": Object.assign({}, getCss(theme, "input.disabled", "select")),
      "&:focus": Object.assign({
        borderRadius: 4,
        border: `1px solid ${borderColor}`
      }, getCss(theme, "input.focused", "select")),
      "&$errorInput": Object.assign({
        borderColor: errorColor,
        "& ~ svg path": {
          stroke: errorColor
        }
      }, getCss(theme, "label.error", "select")),
      "&:hover": Object.assign({}, getCss(theme, "input.hover", "select")),
      "&$readonlyInput ~ svg": {
        display: "none"
      }
    }),
    asterisk: Object.assign({}, getCss(theme, "label.asterisk", "select")),
    required: Object.assign({}, getCss(theme, "label.required", "select")),
    readonlyInput: Object.assign({
      paddingLeft: "0px",
      margin: "0px"
    }, getCss(theme, "input.readOnly", "select")),
    readonlyLabel: Object.assign({
      fontSize: "12px"
    }, getCss(theme, "label.readOnly", "select")),
    options: Object.assign(Object.assign({}, (_c = (_b = (_a = theme.forms) === null || _a === void 0 ? void 0 : _a.select) === null || _b === void 0 ? void 0 : _b.option) === null || _c === void 0 ? void 0 : _c.root), {
      "&:hover": Object.assign({}, (_f = (_e = (_d = theme.forms) === null || _d === void 0 ? void 0 : _d.select) === null || _e === void 0 ? void 0 : _e.option) === null || _f === void 0 ? void 0 : _f.hover)
    }),
    selected: Object.assign(Object.assign({}, (_j = (_h = (_g = theme.forms) === null || _g === void 0 ? void 0 : _g.select) === null || _h === void 0 ? void 0 : _h.optionSelected) === null || _j === void 0 ? void 0 : _j.root), {
      "&:hover": Object.assign({}, (_m = (_l = (_k = theme.forms) === null || _k === void 0 ? void 0 : _k.select) === null || _l === void 0 ? void 0 : _l.optionSelected) === null || _m === void 0 ? void 0 : _m.hover)
    }),
    formHelperText: Object.assign({
      color: errorColor,
      marginLeft: "15px",
      fontSize: "11px",
      lineHeight: "11px"
    }, getCss(theme, "input.helperText", "select")),
    focused: {},
    disabled: {},
    errorLabel: {},
    errorInput: {},
    inputClasses: {
      "&[class*='MuiInput-underline']:before": Object.assign({}, getCss(theme, "input.underline.root", "select")),
      "&[class*='MuiInput-underline']:hover:not($disabled):before": Object.assign({}, getCss(theme, "input.underline.hover", "select")),
      "&[class*='MuiInput-underline']:after": Object.assign({}, getCss(theme, "input.underline.active", "select"))
    },
    iconComponent: Object.assign({
      color: borderColor,
      marginRight: "8px",
      top: "40%"
    }, (_p = (_o = theme.forms) === null || _o === void 0 ? void 0 : _o.select) === null || _p === void 0 ? void 0 : _p.dropdownIcon),
    labelShrink: {}
  };
});

const SelectComponent = props => {
  var _a;
  const parsedDefaultValue = useMemo(() => {
    if (typeof props.value !== "undefined") {
      return props.value;
    }
    return props.defaultValue;
  }, [props.value, props.defaultValue]);
  const {
    value: formValue,
    setValue = noop,
    error,
    errorMessage,
    readOnly,
    setTouched
  } = useFormContext(props, parsedDefaultValue);
  const defaultId = useMemo(() => IdGenerator(), []);
  const {
      name,
      label,
      onChange,
      required = false,
      IconComponent = SelectDownArrowIcon,
      id = defaultId,
      fullWidth,
      options,
      disabled = false,
      disableUnderline = true,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      validations = {},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultMessages,
      value,
      renderOption,
      onFocus,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mountFieldWhileDisabled,
      // do not inject in dom
      shrink: inputShrink
    } = props,
    rest = __rest(props, ["name", "label", "onChange", "required", "IconComponent", "id", "fullWidth", "options", "disabled", "disableUnderline", "validations", "defaultMessages", "value", "renderOption", "onFocus", "mountFieldWhileDisabled", "shrink"]);
  let component;
  const classes = useSelectStyles();
  const handleOnChange = useCallback((event, child) => {
    if (typeof value === "undefined") {
      setValue(event.target.value);
    }
    if (onChange) {
      onChange(event, child);
    }
  }, [setValue, onChange, value]);
  useEffect(() => {
    if (typeof value !== "undefined") {
      setValue(value);
    }
  }, [setValue, value]);
  const optList = options.map(option => {
    const {
      name: optionName,
      value: optionValue,
      disabled: optionDisabled
    } = option;
    let optionContent;
    if (renderOption) {
      optionContent = renderOption(option, formValue === optionValue);
    } else {
      optionContent = React__default.createElement("span", null, optionName);
    }
    return React__default.createElement(MenuItem, {
      key: optionValue,
      value: optionValue,
      disabled: optionDisabled,
      classes: {
        root: classes.options,
        selected: classes.selected
      }
    }, optionContent);
  });
  const handleFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  }, [setTouched, onFocus]);
  if (readOnly || disabled) {
    const selectedValue = ((_a = options.find(_ref => {
      let {
        value: optionId
      } = _ref;
      return optionId === formValue;
    })) === null || _a === void 0 ? void 0 : _a.name) || "";
    return React__default.createElement(InputComponent, {
      name: name,
      readOnly: readOnly,
      disabled: disabled,
      value: selectedValue,
      label: label,
      required: required
    });
  }
  const selectComponent = React__default.createElement(React__default.Fragment, null, React__default.createElement(Select, Object.assign({
    id: id,
    name: name,
    value: formValue || "",
    onChange: handleOnChange,
    classes: {
      root: clsx(classes.input, {
        [classes.errorInput]: error
      }),
      icon: classes.iconComponent
    },
    displayEmpty: true,
    required: required,
    className: classes.inputClasses,
    IconComponent: IconComponent,
    disableUnderline: disableUnderline,
    disabled: disabled,
    MenuProps: {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left"
      },
      getContentAnchorEl: null
    },
    onFocus: handleFocus
  }, rest), optList), React__default.createElement(FormHelperText, {
    className: classes.formHelperText
  }, errorMessage));
  if (label) {
    component = React__default.createElement(FormControl, {
      required: required,
      fullWidth: fullWidth,
      className: classes.formControl,
      disabled: disabled
    }, React__default.createElement(InputLabel, {
      shrink: "shrink" in props ? inputShrink : true,
      error: error,
      classes: {
        root: classes.label,
        required: classes.required,
        asterisk: classes.asterisk,
        focused: classes.labelFocused,
        error: classes.errorLabel,
        shrink: classes.shrink
      },
      htmlFor: id
    }, label), selectComponent);
  } else {
    component = selectComponent;
  }
  return component;
};

const useStyles$1 = makeStyles(theme => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17;
  return {
    formControl: Object.assign({}, getCss(theme, "formControl", "radio")),
    label: Object.assign(Object.assign({
      fontSize: "14px",
      position: "static",
      transform: "none",
      color: labelColor
    }, getCss(theme, "label.root", "radio")), {
      "&$labelDisabled": Object.assign({}, getCss(theme, "label.disabled", "radio")),
      "&$focused": Object.assign({
        color: labelColor
      }, getCss(theme, "label.focused", "radio")),
      "&$errorLabel": Object.assign({
        color: labelColor,
        "& $asterisk": {
          color: errorColor
        }
      }, getCss(theme, "label.error", "radio"))
    }),
    labelDisabled: {},
    errorLabel: {},
    input: Object.assign(Object.assign({
      padding: "5px"
    }, getCss(theme, "input.root", "radio")), {
      "&$disabled": Object.assign({}, getCss(theme, "input.disabled", "radio")),
      "&:hover": Object.assign({
        backgroundColor: "transparent"
      }, getCss(theme, "input.hover", "radio")),
      "&:focus": Object.assign({}, getCss(theme, "input.focused", "radio"))
    }),
    formHelperText: Object.assign({
      color: errorColor,
      marginLeft: "28px",
      fontSize: "11px",
      lineHeight: "11px"
    }, getCss(theme, "input.helperText", "radio")),
    radioGroup: Object.assign({
      marginTop: "8px",
      [getBreakpoints(theme).mobile]: {
        marginTop: "12px"
      }
    }, (_b = (_a = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _a === void 0 ? void 0 : _a.radio) === null || _b === void 0 ? void 0 : _b.radioGroup),
    option: Object.assign(Object.assign({
      marginLeft: 0
    }, (_e = (_d = (_c = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _c === void 0 ? void 0 : _c.radio) === null || _d === void 0 ? void 0 : _d.radioOption) === null || _e === void 0 ? void 0 : _e.root), {
      "&:hover": Object.assign({}, (_h = (_g = (_f = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _f === void 0 ? void 0 : _f.radio) === null || _g === void 0 ? void 0 : _g.radioOption) === null || _h === void 0 ? void 0 : _h.hover),
      "&$optionChecked": Object.assign(Object.assign({}, (_m = (_l = (_k = (_j = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _j === void 0 ? void 0 : _j.radio) === null || _k === void 0 ? void 0 : _k.radioOption) === null || _l === void 0 ? void 0 : _l.checked) === null || _m === void 0 ? void 0 : _m.root), {
        "&:hover": Object.assign({}, (_r = (_q = (_p = (_o = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _o === void 0 ? void 0 : _o.radio) === null || _p === void 0 ? void 0 : _p.radioOption) === null || _q === void 0 ? void 0 : _q.checked) === null || _r === void 0 ? void 0 : _r.hover)
      })
    }),
    optionChecked: {},
    optionLabel: Object.assign(Object.assign({
      fontSize: "12px",
      marginLeft: "0px"
    }, (_u = (_t = (_s = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _s === void 0 ? void 0 : _s.radio) === null || _t === void 0 ? void 0 : _t.radioLabel) === null || _u === void 0 ? void 0 : _u.root), {
      "&:hover": Object.assign({}, (_x = (_w = (_v = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _v === void 0 ? void 0 : _v.radio) === null || _w === void 0 ? void 0 : _w.radioLabel) === null || _x === void 0 ? void 0 : _x.hover),
      "&$optionChecked": Object.assign(Object.assign({}, (_1 = (_0 = (_z = (_y = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _y === void 0 ? void 0 : _y.radio) === null || _z === void 0 ? void 0 : _z.radioLabel) === null || _0 === void 0 ? void 0 : _0.checked) === null || _1 === void 0 ? void 0 : _1.root), {
        "&:hover": Object.assign({}, (_5 = (_4 = (_3 = (_2 = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _2 === void 0 ? void 0 : _2.radio) === null || _3 === void 0 ? void 0 : _3.radioLabel) === null || _4 === void 0 ? void 0 : _4.checked) === null || _5 === void 0 ? void 0 : _5.hover)
      })
    }),
    actionLabel: {
      fontSize: "14px"
    },
    required: Object.assign({}, getCss(theme, "label.required", "radio")),
    asterisk: Object.assign({}, getCss(theme, "label.asterisk", "radio")),
    readonlyLabel: Object.assign({
      fontSize: "12px"
    }, getCss(theme, "label.readOnly", "radio")),
    readonlyInput: Object.assign({
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "14px",
      marginTop: "6px"
    }, getCss(theme, "input.readOnly", "radio")),
    focused: {},
    disabled: {},
    root: {},
    icon: Object.assign({
      borderRadius: "50%",
      width: 18,
      height: 18,
      boxShadow: "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
      backgroundColor: "#f5f8fa",
      backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))"
    }, (_7 = (_6 = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _6 === void 0 ? void 0 : _6.radio) === null || _7 === void 0 ? void 0 : _7.uncheckedIcon),
    checkedIcon: Object.assign({
      backgroundColor: "#FFF",
      backgroundImage: "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
      "&:before": {
        display: "block",
        width: 18,
        height: 18,
        backgroundImage: `radial-gradient(${theme.palette.primary.main} 38%,transparent 32%)`,
        content: '""'
      },
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}, inset 0 -1px 0 ${theme.palette.primary.main}`,
      "input:hover ~ &": {
        backgroundColor: "none"
      }
    }, (_9 = (_8 = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _8 === void 0 ? void 0 : _8.radio) === null || _9 === void 0 ? void 0 : _9.checkedIcon),
    errorLabelOption: Object.assign({}, (_13 = (_12 = (_11 = (_10 = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _10 === void 0 ? void 0 : _10.radio) === null || _11 === void 0 ? void 0 : _11.input) === null || _12 === void 0 ? void 0 : _12.error) === null || _13 === void 0 ? void 0 : _13.labelOption),
    errorCheckOption: Object.assign({
      "& $icon": {
        boxShadow: `inset 0 0 0 1px ${errorColor}, inset 0 -1px 0 ${errorColor}`
      }
    }, (_17 = (_16 = (_15 = (_14 = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _14 === void 0 ? void 0 : _14.radio) === null || _15 === void 0 ? void 0 : _15.input) === null || _16 === void 0 ? void 0 : _16.error) === null || _17 === void 0 ? void 0 : _17.checkOption)
  };
});

/* eslint-disable react/destructuring-assignment */
const RadioComponent = props => {
  const {
    error,
    errorMessage,
    setValue = noop,
    value: formValue,
    readOnly,
    setTouched
  } = useFormContext(props, typeof props.value === "undefined" ? props.defaultValue || "" : props.value);
  const {
    label = "",
    name = "",
    onChange,
    options,
    required = false,
    disabled = false,
    radioProps,
    value
  } = props;
  let component;
  const classes = useStyles$1();
  useEffect(() => {
    if (typeof value !== "undefined") {
      setValue(value);
    }
  }, [setValue, value]);
  const handleOnChange = useCallback(e => {
    if (typeof value === "undefined") {
      setValue(e.target.value);
    }
    if (onChange) {
      onChange(e);
    }
  }, [setValue, onChange, value]);
  useEffect(() => {
    // if the option selected is disabled
    if (typeof value === "undefined" && options.filter(x => x.disabled).find(y => y.value === formValue)) {
      // set the value state after disable validation
      setValue("");
    }
  }, [options, formValue, value, setValue]);
  const handleFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (radioProps === null || radioProps === void 0 ? void 0 : radioProps.onFocus) {
      radioProps.onFocus(e);
    }
  }, [radioProps, setTouched]);
  const newOptions = options.map(option => {
    const {
      value: optionValue,
      label: optionLabel,
      labelPlace,
      id,
      name: optionName,
      disabled: optionDisabled
    } = option;
    return React__default.createElement(FormControlLabel, {
      key: optionValue,
      value: optionValue,
      control: React__default.createElement(Radio, Object.assign({
        name: optionName,
        id: id,
        icon: React__default.createElement("span", {
          className: classes.icon
        }),
        checkedIcon: React__default.createElement("span", {
          className: clsx(classes.icon, classes.checkedIcon)
        }),
        classes: {
          root: clsx(classes.input, {
            [classes.readonlyInput]: readOnly,
            [classes.errorCheckOption]: error
          })
        },
        color: "primary",
        disabled: optionDisabled
      }, radioProps, {
        onFocus: handleFocus
      })),
      classes: {
        root: clsx(classes.option, {
          [classes.optionChecked]: formValue === optionValue
        }),
        label: clsx(classes.optionLabel, {
          [classes.required]: required,
          [classes.readonlyLabel]: readOnly,
          [classes.errorLabelOption]: error,
          [classes.actionLabel]: props.label,
          [classes.optionChecked]: formValue === optionValue
        })
      },
      label: optionLabel,
      labelPlacement: labelPlace || "end"
    });
  });
  const selectedValue = useMemo(() => {
    var _a;
    return ((_a = options.find(_ref => {
      let {
        value: id
      } = _ref;
      return id === formValue;
    })) === null || _a === void 0 ? void 0 : _a.label) || "-";
  }, [options, formValue]);
  if (readOnly || disabled) {
    return React__default.createElement(InputComponent, {
      name: "readOnlyInput",
      required: required,
      readOnly: readOnly,
      label: label,
      value: selectedValue,
      disabled: disabled
    });
  }
  const inputComponent = React__default.createElement(RadioGroup, {
    classes: {
      root: classes.radioGroup
    },
    name: name,
    value: formValue,
    onChange: handleOnChange,
    row: true
  }, newOptions);
  if (label) {
    component = React__default.createElement(FormControl, {
      required: required,
      className: classes.formControl
    }, React__default.createElement(FormLabel, {
      error: error,
      classes: {
        root: clsx(classes.label, {
          [classes.readonlyLabel]: readOnly
        }),
        focused: classes.focused,
        required: classes.required,
        asterisk: classes.asterisk,
        error: classes.errorLabel
      }
    }, React__default.createElement("span", null, label)), inputComponent, errorMessage && React__default.createElement(FormHelperText, {
      className: classes.formHelperText
    }, errorMessage));
  } else {
    component = inputComponent;
  }
  return component;
};

const useStyles = makeStyles(theme => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
  return {
    formControl: Object.assign({}, getCss(theme, "formControl", "switch")),
    label: Object.assign(Object.assign({
      transform: "none",
      position: "static",
      fontSize: "14px",
      color: labelColor
    }, getCss(theme, "label.root", "switch")), {
      "&$labelDisabled": Object.assign({}, getCss(theme, "label.disabled", "switch")),
      "&$labelFocused": Object.assign({
        color: labelColor
      }, getCss(theme, "label.focused", "switch")),
      "&$errorLabel": Object.assign({
        color: labelColor,
        "& $asterisk": {
          color: errorColor
        }
      }, getCss(theme, "label.error", "switch"))
    }),
    labelFocused: {},
    labelDisabled: {},
    errorLabel: {},
    controlRoot: Object.assign({
      marginLeft: 0,
      marginTop: "12px",
      [getBreakpoints(theme).mobile]: {
        marginTop: "17px"
      }
    }, (_b = (_a = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _a === void 0 ? void 0 : _a.switch) === null || _b === void 0 ? void 0 : _b.controlRoot),
    controlLabel: Object.assign({
      fontSize: "14px"
    }, (_d = (_c = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _c === void 0 ? void 0 : _c.switch) === null || _d === void 0 ? void 0 : _d.controlLabel),
    root: Object.assign(Object.assign({
      marginRight: "10px",
      width: "46px",
      height: "20px",
      padding: "0px",
      display: "flex"
    }, (_f = (_e = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _e === void 0 ? void 0 : _e.switch) === null || _f === void 0 ? void 0 : _f.root), {
      "&:focus": Object.assign({}, getCss(theme, "input.focused", "switch")),
      "&:hover": Object.assign({}, getCss(theme, "input.hover", "switch")),
      "&$disabled": Object.assign({}, getCss(theme, "input.disabled", "switch"))
    }),
    disabled: {},
    focused: {},
    switchBase: Object.assign(Object.assign({
      padding: "2px",
      color: lightColor
    }, (_h = (_g = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _g === void 0 ? void 0 : _g.switch) === null || _h === void 0 ? void 0 : _h.switchBase), {
      "&$checked": Object.assign({
        color: lightColor,
        transform: "translateX(26px)",
        "& + $track": {
          opacity: "1",
          backgroundColor: theme.palette.primary.main
        }
      }, (_k = (_j = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _j === void 0 ? void 0 : _j.switch) === null || _k === void 0 ? void 0 : _k.checked)
    }),
    thumb: Object.assign({
      width: "16px",
      height: "16px",
      boxShadow: "none"
    }, (_m = (_l = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _l === void 0 ? void 0 : _l.switch) === null || _m === void 0 ? void 0 : _m.thumb),
    track: Object.assign({
      borderRadius: "47px",
      opacity: "1",
      backgroundColor: offColor
    }, (_p = (_o = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _o === void 0 ? void 0 : _o.switch) === null || _p === void 0 ? void 0 : _p.track),
    checked: {},
    required: Object.assign({}, getCss(theme, "label.required", "switch")),
    asterisk: Object.assign({
      marginLeft: 5
    }, getCss(theme, "label.asterisk", "switch")),
    readonlyLabel: Object.assign({
      fontSize: "12px",
      color: labelColor
    }, getCss(theme, "label.readOnly", "switch")),
    readonlyInput: Object.assign({
      margin: "0px",
      "& input": {
        padding: "0px",
        alignSelf: "self-start"
      }
    }, getCss(theme, "input.readOnly", "switch")),
    formHelperText: Object.assign({
      color: errorColor,
      marginLeft: "57px",
      fontSize: "11px",
      lineHeight: "11px"
    }, getCss(theme, "input.helperText", "switch"))
  };
});

const SwitchComponent = props => {
  const {
    value,
    setValue = noop,
    error,
    errorMessage,
    readOnly,
    setTouched
  } = useFormContext(props, props.option.checked);
  const {
    label = "",
    name = "",
    labelPlace,
    option = {
      switchLabel: "",
      checked: false,
      id: "",
      disabled: false
    },
    onChange,
    disabled = false,
    required = false,
    onFocus,
    mountFieldWhileDisabled // do not inject in dom
  } = props;
  const handleFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  }, [setTouched, onFocus]);
  let component;
  const classes = useStyles();
  const handleChange = useCallback(e => {
    setValue(e.target.checked);
    if (onChange) {
      onChange(e);
    }
  }, [setValue, onChange]);
  if (readOnly || disabled) {
    return React__default.createElement(InputComponent, {
      readOnly: readOnly,
      name: "readOnlyEmpty",
      defaultValue: value ? "Sim" : "Não",
      required: required,
      label: label,
      disabled: disabled
    });
  }
  let inputComponent = React__default.createElement(FormControlLabel, {
    labelPlacement: labelPlace || "end",
    classes: {
      root: classes.controlRoot,
      label: classes.controlLabel
    },
    control: React__default.createElement(Switch, {
      checked: value,
      onChange: handleChange,
      onFocus: handleFocus,
      name: name,
      classes: {
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked
      }
    }),
    label: option.switchLabel
  });
  if (label) {
    component = React__default.createElement(FormControl, {
      className: classes.formControl
    }, React__default.createElement(FormLabel, {
      error: error,
      required: required,
      focused: readOnly ? false : undefined,
      classes: {
        root: classes.label,
        required: classes.required,
        asterisk: classes.asterisk,
        error: classes.errorLabel,
        focused: classes.labelFocused
      }
    }, React__default.createElement("span", null, label)), inputComponent, React__default.createElement(FormHelperText, {
      className: classes.formHelperText
    }, errorMessage));
  } else {
    component = inputComponent;
  }
  return component;
};

const PopperComponent = _a => {
  var {
      customProps
    } = _a,
    props = __rest(_a, ["customProps"]);
  const {
    anchorEl
  } = props;
  let anchor;
  if (typeof anchorEl === "function") {
    anchor = anchorEl();
  } else {
    anchor = anchorEl;
  }
  let width;
  if (anchor) {
    const element = anchor;
    const clientRects = element.getClientRects();
    if (clientRects.length) {
      width = clientRects[0].width;
    } else {
      width = element.clientWidth;
    }
  }
  const mergedProps = useMemo(() => merge({
    disablePortal: true,
    placement: "bottom-start",
    modifiers: {
      preventOverflow: {
        escapeWithReference: true
      }
    },
    style: {
      width
    }
  }, customProps), [customProps, width]);
  return React__default.createElement(Popper, Object.assign({}, props, mergedProps));
};

const getSelectSearchStyles = mobileFullScreen => {
  return makeStyles(theme => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17, _18, _19, _20, _21, _22, _23, _24, _25, _26;
    const breakpoints = getBreakpoints(theme);
    let mobileFullScreenDropdown;
    let selectBoxContainer;
    if (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.enable) {
      mobileFullScreenDropdown = Object.assign({
        position: "fixed !important",
        top: "50px !important",
        bottom: "0 !important",
        left: "0 !important",
        right: "0 !important",
        width: "100vw !important",
        transform: "none !important",
        backgroundColor: "white",
        "& ul, & > div": {
          height: "100%",
          maxHeight: "none"
        }
      }, (_c = (_b = (_a = theme.forms) === null || _a === void 0 ? void 0 : _a.selectSearch) === null || _b === void 0 ? void 0 : _b.mobileFullScreen) === null || _c === void 0 ? void 0 : _c.dropdown);
      selectBoxContainer = Object.assign({
        position: "fixed",
        left: 0,
        right: 0,
        top: 0,
        height: "50px",
        backgroundColor: "white",
        margin: 0,
        border: 0,
        borderRadius: 0,
        zIndex: 1000,
        marginTop: "0 !important"
      }, (_f = (_e = (_d = theme.forms) === null || _d === void 0 ? void 0 : _d.selectSearch) === null || _e === void 0 ? void 0 : _e.mobileFullScreen) === null || _f === void 0 ? void 0 : _f.input);
    }
    const mobileFullScreenBreakpoint = (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.breakpoint) ? theme.breakpoints.down(mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.breakpoint) : getBreakpoints(theme).mobile;
    return {
      formControl: Object.assign({
        width: "100%"
      }, getCss(theme, "formControl", "selectSearch")),
      textField: {
        "& label": Object.assign(Object.assign({
          fontSize: "14px",
          transform: "none",
          position: "static",
          color: labelColor,
          "& + $input": {
            marginTop: "6px"
          },
          "&$readonlyLabel": Object.assign({
            fontSize: "12px"
          }, getCss(theme, "label.readOnly", "selectSearch"))
        }, getCss(theme, "label.root", "selectSearch")), {
          "&$focused": Object.assign({
            color: labelColor
          }, getCss(theme, "label.focused", "selectSearch")),
          "&$disabled": Object.assign({}, getCss(theme, "label.disabled", "selectSearch")),
          "&$errorLabel": Object.assign({
            color: labelColor,
            "& $asterisk": {
              color: errorColor
            }
          }, getCss(theme, "label.error", "selectSearch")),
          "&$labelShrink": Object.assign({}, getCss(theme, "label.shrink", "selectSearch"))
        }),
        "&:hover label": Object.assign({}, getCss(theme, "label.hover", "selectSearch"))
      },
      input: Object.assign(Object.assign({
        minHeight: "32px",
        [breakpoints.mobile]: {
          minHeight: "41px"
        },
        "&$inputMobileFullScreen": {
          [mobileFullScreenBreakpoint]: Object.assign({}, selectBoxContainer)
        },
        paddingTop: "1px",
        paddingLeft: "14px",
        fontSize: "14px",
        borderRadius: 4,
        border: `1px solid ${borderColor}`
      }, getCss(theme, "input.root", "selectSearch")), {
        "&$focused": Object.assign({}, getCss(theme, "input.focused", "selectSearch")),
        "&$disabled": Object.assign({}, getCss(theme, "input.disabled", "selectSearch")),
        "&$errorInput": Object.assign({
          borderColor: errorColor,
          "& path": {
            stroke: errorColor
          }
        }, getCss(theme, "input.error", "selectSearch")),
        "&$underline:before": Object.assign({}, getCss(theme, "input.underline.root", "selectSearch")),
        "&$underline:hover:not($disabled):before": Object.assign({}, getCss(theme, "input.underline.hover", "selectSearch")),
        "&$underline:after": Object.assign({}, getCss(theme, "input.underline.active", "selectSearch")),
        "&:hover": Object.assign({}, getCss(theme, "input.hover", "selectSearch")),
        "&$open": Object.assign({}, (_h = (_g = theme.forms) === null || _g === void 0 ? void 0 : _g.selectSearch) === null || _h === void 0 ? void 0 : _h.open),
        "&$openFlipped": Object.assign({}, (_k = (_j = theme.forms) === null || _j === void 0 ? void 0 : _j.selectSearch) === null || _k === void 0 ? void 0 : _k.openFlipped)
      }),
      asterisk: Object.assign({}, getCss(theme, "label.asterisk", "selectSearch")),
      required: Object.assign({}, getCss(theme, "label.required", "selectSearch")),
      readonlyInput: Object.assign({
        fontWeight: "bold",
        fontSize: "14px",
        marginTop: "6px",
        lineHeight: "14px"
      }, getCss(theme, "input.readOnly", "selectSearch")),
      readonlyLabel: {},
      options: Object.assign(Object.assign({}, (_o = (_m = (_l = theme.forms) === null || _l === void 0 ? void 0 : _l.selectSearch) === null || _m === void 0 ? void 0 : _m.option) === null || _o === void 0 ? void 0 : _o.root), {
        "&:hover": Object.assign({}, (_r = (_q = (_p = theme.forms) === null || _p === void 0 ? void 0 : _p.selectSearch) === null || _q === void 0 ? void 0 : _q.option) === null || _r === void 0 ? void 0 : _r.hover),
        "&[data-focus=true]": Object.assign({}, (_u = (_t = (_s = theme.forms) === null || _s === void 0 ? void 0 : _s.selectSearch) === null || _t === void 0 ? void 0 : _t.option) === null || _u === void 0 ? void 0 : _u.focus),
        "&[aria-selected=true]": Object.assign(Object.assign({}, (_x = (_w = (_v = theme.forms) === null || _v === void 0 ? void 0 : _v.selectSearch) === null || _w === void 0 ? void 0 : _w.optionSelected) === null || _x === void 0 ? void 0 : _x.root), {
          "&:hover": Object.assign({}, (_0 = (_z = (_y = theme.forms) === null || _y === void 0 ? void 0 : _y.selectSearch) === null || _z === void 0 ? void 0 : _z.optionSelected) === null || _0 === void 0 ? void 0 : _0.hover)
        })
      }),
      formHelperText: Object.assign({
        fontSize: "11px",
        lineHeight: "11px",
        marginLeft: "15px",
        "&.Mui-error": {
          color: errorColor
        }
      }, getCss(theme, "input.helperText", "selectSearch")),
      focused: {},
      disabled: {},
      errorLabel: {},
      errorInput: {},
      underline: {},
      dropdown: Object.assign(Object.assign({}, (_2 = (_1 = theme.forms) === null || _1 === void 0 ? void 0 : _1.selectSearch) === null || _2 === void 0 ? void 0 : _2.dropdown), {
        "&[x-placement='top-start']": Object.assign(Object.assign({}, (_4 = (_3 = theme.forms) === null || _3 === void 0 ? void 0 : _3.selectSearch) === null || _4 === void 0 ? void 0 : _4.dropdownFlipped), {
          "&$dropdownMobileFullScreen": {
            [mobileFullScreenBreakpoint]: Object.assign({}, mobileFullScreenDropdown)
          }
        }),
        "&$dropdownMobileFullScreen": {
          [mobileFullScreenBreakpoint]: Object.assign({}, mobileFullScreenDropdown)
        }
      }),
      open: {},
      openFlipped: {},
      groupUl: Object.assign({}, (_7 = (_6 = (_5 = theme.forms) === null || _5 === void 0 ? void 0 : _5.selectSearch) === null || _6 === void 0 ? void 0 : _6.group) === null || _7 === void 0 ? void 0 : _7.root),
      groupLabel: Object.assign({}, (_10 = (_9 = (_8 = theme.forms) === null || _8 === void 0 ? void 0 : _8.selectSearch) === null || _9 === void 0 ? void 0 : _9.group) === null || _10 === void 0 ? void 0 : _10.label),
      tag: Object.assign({
        margin: "0px 15px 0px -9px",
        maxHeight: "25px",
        fontSize: "14px"
      }, (_13 = (_12 = (_11 = theme.forms) === null || _11 === void 0 ? void 0 : _11.selectSearch) === null || _12 === void 0 ? void 0 : _12.tag) === null || _13 === void 0 ? void 0 : _13.root),
      popupIndicator: Object.assign({
        padding: "10px 7px",
        marginRight: "5px"
      }, (_15 = (_14 = theme.forms) === null || _14 === void 0 ? void 0 : _14.selectSearch) === null || _15 === void 0 ? void 0 : _15.dropdownIcon),
      clearIndicator: Object.assign({
        padding: "8px",
        "& path": {
          fill: borderColor
        }
      }, (_17 = (_16 = theme.forms) === null || _16 === void 0 ? void 0 : _16.selectSearch) === null || _17 === void 0 ? void 0 : _17.clearIcon),
      clearIndicatorDirty: Object.assign({}, (_19 = (_18 = theme.forms) === null || _18 === void 0 ? void 0 : _18.selectSearch) === null || _19 === void 0 ? void 0 : _19.clearIndicatorDirty),
      noOptions: Object.assign({}, (_21 = (_20 = theme.forms) === null || _20 === void 0 ? void 0 : _20.selectSearch) === null || _21 === void 0 ? void 0 : _21.noOptions),
      loading: Object.assign({}, (_23 = (_22 = theme.forms) === null || _22 === void 0 ? void 0 : _22.selectSearch) === null || _23 === void 0 ? void 0 : _23.loading),
      dropdownMobileFullScreen: {},
      inputMobileFullScreen: {},
      mobileFullScreenCloseIcon: Object.assign({
        padding: "8px",
        "& path": {
          fill: borderColor
        }
      }, (_26 = (_25 = (_24 = theme.forms) === null || _24 === void 0 ? void 0 : _24.selectSearch) === null || _25 === void 0 ? void 0 : _25.mobileFullScreen) === null || _26 === void 0 ? void 0 : _26.closeIcon),
      labelShrink: {}
    };
  });
};

/* eslint-disable no-param-reassign */
const selectSearchActionHandler = (previousState, action, config, messages, multiple, sortOptions, disableLoading) => {
  const newState = Object.assign({}, previousState);
  switch (action.type) {
    case "search":
      if (action.newSearch) {
        searchAction(newState, config, messages, action.newSearch);
      }
      break;
    case "setError":
      newState.open = false;
      newState.loading = false;
      break;
    case "setResults":
      if (action.result && action.page && action.searchId) {
        setResultsAction(newState, config, action.result, action.searchId, action.page, sortOptions, action.search, action.scrolling);
      }
      break;
    case "setCachedResults":
      if (config.cacheResults && action.resultCache && action.searchId) {
        setCachedResultAction(newState, action.resultCache, action.searchId, sortOptions);
      }
      break;
    case "close":
      closeAction(newState);
      if (multiple) {
        newState.inputValue = "";
      }
      break;
    case "onInputChange":
      if (typeof action.searchValue !== "undefined") {
        onInputChangeAction(newState, config, messages, action.searchValue);
      }
      break;
    case "open":
      onOpenAction(newState, config, messages);
      break;
    case "onChange":
      if (action.valueComponent || action.valueComponent === null) {
        newState.valueComponent = action.valueComponent;
        if (!newState.valueComponent && !newState.open && !multiple) {
          newState.inputValue = "";
        }
        newState.options = filterSelectedOptions(newState, newState.options, sortOptions);
      }
      break;
    case "resolveData":
      if (action.searchId === newState.searchId && action.newSearch && newState.open) {
        newSearchAction(newState, Object.assign(Object.assign({}, action.newSearch), {
          immediate: true
        }));
      }
      newState.loading = action.loading || false;
      break;
    case "loading":
      newState.loading = !disableLoading && action.loading || false;
      break;
    case "changeFlipState":
      newState.flipped = Boolean(action.flipped);
      break;
    case "setOptions":
      if (action.options) {
        newState.options = action.options;
      }
      break;
  }
  return newState;
};
const searchAction = (state, config, messages, newSearch) => {
  const noOptionsMessage = getNoOptionsMessage(config, newSearch.search);
  state.noOptionsMessage = noOptionsMessage || messages.noOptions;
  if (state.customResolver) {
    if (noOptionsMessage) {
      state.options = [];
      state.searchId += 1;
      state.newSearch = undefined;
    } else {
      newSearchAction(state, newSearch);
    }
  }
};
const getNoOptionsMessage = (config, value) => {
  var _a;
  const {
    length
  } = value;
  if (length < config.strLenForSearch) {
    const remainingLetters = config.strLenForSearch - length;
    const callback = (_a = config === null || config === void 0 ? void 0 : config.messages) === null || _a === void 0 ? void 0 : _a.onMissingLettersToSearch;
    if (callback) {
      return callback(remainingLetters);
    }
    return `Required at least ${remainingLetters} letters to search`;
  }
  return false;
};
const setResultsAction = function (state, config, result, searchId, page, sortOptions) {
  let search = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : "";
  let scrolling = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : false;
  if (state.open && searchId === state.searchId) {
    state.loading = false;
    if (result.error) {
      console.log(result.error);
    } else if (result.result) {
      let newOptions = result.result.options;
      if (scrolling) {
        newOptions = [...state.options, ...newOptions];
      }
      state.options = filterSelectedOptions(state, newOptions, sortOptions);
      state.nextPage = page * config.optionsSize < result.result.totalCount ? page + 1 : false;
      if (config.cacheResults) {
        state.cachedResults.push({
          options: result.result.options,
          nextPage: state.nextPage,
          scrolling,
          page,
          search
        });
      }
    }
  }
};
const newSearchAction = (state, newSearch) => {
  state.searchId += 1;
  state.newSearch = newSearch;
};
const closeAction = state => {
  state.open = false;
  state.loading = false;
  state.searchId += 1;
  state.newSearch = undefined;
};
const onInputChangeAction = (state, config, messages, search) => {
  state.inputValue = search;
  searchAction(state, config, messages, {
    search
  });
};
const onOpenAction = (state, config, messages) => {
  state.open = true;
  searchAction(state, config, messages, {
    search: state.inputValue
  });
};
const getParsedOptions = value => {
  if (value) {
    if (!Array.isArray(value)) {
      return [value];
    }
    return value;
  }
  return [];
};
const setCachedResultAction = (state, _ref, searchId, sortOptions) => {
  let {
    options,
    scrolling,
    nextPage
  } = _ref;
  if (state.open && searchId === state.searchId) {
    let newOptions = options;
    if (scrolling) {
      newOptions = [...state.options, ...newOptions];
    }
    state.options = filterSelectedOptions(state, newOptions, sortOptions);
    state.nextPage = nextPage;
    state.loading = false;
  }
};
const filterSelectedOptions = (state, options, sortOptions) => {
  const parsedSelectedOptions = getParsedOptions(state.valueComponent);
  const selectedIds = parsedSelectedOptions.map(option => typeof option === "object" ? option.id : option);
  return [...parsedSelectedOptions, ...options.filter(option => !selectedIds.includes(typeof option === "object" ? option.id : option)).sort(sortOptions !== null && sortOptions !== void 0 ? sortOptions : sortOptionsDefault)];
};
const sortOptionsDefault = (value1, value2) => naturalCompare(typeof value1 === "object" ? value1.value : value1.toString(), typeof value2 === "object" ? value2.value : value2.toString());

const isVisibleLastOption = event => {
  const {
    lastChild
  } = event.target;
  const parentTop = event.target.getBoundingClientRect().top;
  const parentHeight = event.target.getBoundingClientRect().height;
  const lastChildBottom = lastChild.getBoundingClientRect().bottom - 1;
  return lastChildBottom < parentTop + parentHeight;
};
const defaultConfig = {
  selectConfig: {},
  strLenForSearch: 3,
  optionsSize: 15,
  delaySearch: 0,
  cacheResults: true,
  messages: {
    loading: `Loading`,
    noOptions: `No Option`
  }
};
const SelectSearch = props => {
  var _a;
  const defaultValueComponent = useMemo(() => parseValue$1(typeof props.value === "undefined" ? props.defaultValue : props.value, props.dataResolver, props.multiple || false), [props.defaultValue, props.dataResolver, props.multiple, props.value]);
  const defaultValue = useMemo(() => {
    if (defaultValueComponent) {
      if (isSelectOption(defaultValueComponent)) {
        return defaultValueComponent.id;
      }
      return defaultValueComponent.map(option => typeof option === "object" ? option.id : option);
    }
    return null;
  }, [defaultValueComponent]);
  const {
    error,
    errorMessage,
    setValue = noop,
    resetForm,
    readOnly,
    value,
    setTouched
  } = useFormContext(props, defaultValue);
  const {
      name,
      label = "",
      configuration = {},
      dataResolver,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      validations = {},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultMessages,
      required = false,
      disabled = false,
      shrink: inputShrink,
      disableUnderline = true,
      sortOptions,
      onChangeSelect = noop,
      onChangeMultipleSelect = noop,
      endAdornments,
      autoCompleteOverride,
      renderOption,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultValue: defaultValueProp,
      // must be defined so it's not injected into the component
      popperProps,
      multiple = false,
      ChipProps,
      blurOnSelect,
      dropdownIcon = React__default.createElement(SelectDownArrowIcon, null),
      filterOptions,
      value: inputValue,
      getOptionDisabled,
      freeSolo,
      onInputChange,
      mobileFullScreen,
      onClose = noop,
      onOpen = noop,
      startAdornments,
      openOnFocus = true,
      renderTags,
      onFocus,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mountFieldWhileDisabled,
      // do not inject in dom
      autoHighlight,
      disableCloseOnSelect,
      filterSelectedOptions = true,
      onClick,
      disableLoading
    } = props,
    inputParams = __rest(props, ["name", "label", "configuration", "dataResolver", "validations", "defaultMessages", "required", "disabled", "shrink", "disableUnderline", "sortOptions", "onChangeSelect", "onChangeMultipleSelect", "endAdornments", "autoCompleteOverride", "renderOption", "defaultValue", "popperProps", "multiple", "ChipProps", "blurOnSelect", "dropdownIcon", "filterOptions", "value", "getOptionDisabled", "freeSolo", "onInputChange", "mobileFullScreen", "onClose", "onOpen", "startAdornments", "openOnFocus", "renderTags", "onFocus", "mountFieldWhileDisabled", "autoHighlight", "disableCloseOnSelect", "filterSelectedOptions", "onClick", "disableLoading"]);
  const handleFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  }, [setTouched, onFocus]);
  const theme = useTheme();
  const mobileFullScreenBreakpoint = (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.breakpoint) ? theme.breakpoints.down(mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.breakpoint) : getBreakpoints(theme).mobile;
  const mobileFullScreenMode = useMediaQuery(mobileFullScreenBreakpoint, {
    noSsr: true
  }) && (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.enable);
  useEffect(() => {
    if (typeof inputValue !== "undefined" && !isEqual(inputValue, value)) {
      const parsedValue = parseValue$1(inputValue, dataResolver, multiple);
      dispatchAction({
        type: "onChange",
        valueComponent: parsedValue
      });
      setValueComponent(parsedValue, multiple, setValue);
    }
  }, [inputValue, dataResolver, multiple, setValue, value]);
  const defaultOptions = useMemo(() => {
    if (typeof dataResolver === "function") {
      if (defaultValueComponent) {
        if (isSelectOption(defaultValueComponent)) {
          return [defaultValueComponent];
        }
        return defaultValueComponent;
      }
      return [];
    }
    return dataResolver;
  }, [dataResolver, defaultValueComponent]);
  const useStyles = useMemo(() => getSelectSearchStyles(mobileFullScreen), [mobileFullScreen]);
  const classes = useStyles();
  const config = useMemo(() => merge({}, defaultConfig, configuration), [configuration]);
  const message = useMemo(() => config.messages, [config]);
  const actionHandlerProxy = useCallback((state, action) => {
    return selectSearchActionHandler(state, action, config, message, multiple, sortOptions, disableLoading);
  }, [config, disableLoading, message, multiple, sortOptions]);
  const [state, dispatchAction] = useReducer(actionHandlerProxy, {
    loading: false,
    inputValue: "",
    nextPage: 1,
    noOptionsMessage: getNoOptionsMessage(config, "") || message.noOptions,
    open: false,
    flipped: false,
    options: defaultOptions,
    customResolver: typeof dataResolver === "function",
    searchId: 1,
    valueComponent: defaultValueComponent,
    cachedResults: []
  });
  const mountedRef = useRef(true);
  const closeCallbackRef = useRef(onClose);
  const delayRef = useRef(0);
  closeCallbackRef.current = onClose;
  const cleanup = useCallback(() => {
    mountedRef.current = false;
    closeCallbackRef.current();
  }, []);
  useEffect(() => {
    return cleanup;
  }, [cleanup]);
  const dataHandler = useCallback((searchId, _ref) => {
    let {
      search,
      page = 1,
      scrolling
    } = _ref;
    if (typeof dataResolver === "function") {
      let searchMathSelectedValue = false;
      if (state.valueComponent && !Array.isArray(state.valueComponent)) {
        const {
          valueComponent
        } = state;
        searchMathSelectedValue = (typeof valueComponent === "object" ? valueComponent.value : valueComponent) === search;
      }
      const parsedSearch = searchMathSelectedValue ? "" : search;
      if (config.cacheResults) {
        const resultCache = getResultCache(state.cachedResults, {
          search: parsedSearch,
          page
        });
        if (resultCache) {
          dispatchAction({
            type: "setCachedResults",
            resultCache,
            searchId
          });
          return;
        }
      }
      dispatchAction({
        type: "loading",
        loading: true
      });
      const pagination = {
        page,
        size: config.optionsSize
      };
      dataResolver(searchId, pagination, parsedSearch).then(data => {
        if (mountedRef.current) {
          dispatchAction({
            type: "setResults",
            searchId,
            result: data,
            page,
            search: parsedSearch,
            scrolling
          });
        }
      }).catch(() => {
        dispatchAction({
          type: "setError"
        });
      });
    }
  }, [dataResolver, config, state]);
  const handleNewSearch = useCallback((searchId, newSearch) => {
    if (delayRef.current) {
      clearTimeout(delayRef.current);
    }
    if (newSearch.immediate) {
      dataHandler(searchId, newSearch);
    } else {
      delayRef.current = window.setTimeout(() => {
        if (mountedRef.current) {
          delayRef.current = 0;
          dispatchAction({
            type: "resolveData",
            searchId,
            newSearch
          });
        }
      }, config.delaySearch);
    }
  }, [dataHandler, config.delaySearch]);
  const currentSearchIdRef = useRef(state.searchId);
  useEffect(() => {
    if (state.newSearch && currentSearchIdRef.current !== state.searchId) {
      handleNewSearch(state.searchId, state.newSearch);
    }
    currentSearchIdRef.current = state.searchId;
  }, [handleNewSearch, state.newSearch, state.searchId]);
  const lastSearchTriggeredByScrollRef = useRef();
  const scrollCallback = useCallback(event => {
    const newSearch = {
      search: state.inputValue,
      page: state.nextPage,
      scrolling: true,
      immediate: true
    };
    if (isVisibleLastOption(event) && state.nextPage !== false && !isEqual(lastSearchTriggeredByScrollRef.current, newSearch)) {
      lastSearchTriggeredByScrollRef.current = newSearch;
      dispatchAction({
        type: "search",
        newSearch
      });
    }
  }, [state]);
  const handleOnInputChange = useCallback((_1, searchValue, reason) => {
    if (onInputChange) {
      onInputChange(searchValue);
    }
    if (!multiple || reason !== "reset") {
      dispatchAction({
        type: "onInputChange",
        searchValue
      });
    }
  }, [onInputChange, multiple]);
  const handleOpen = useCallback(() => {
    dispatchAction({
      type: "open"
    });
    onOpen();
  }, [onOpen]);
  const handleClose = useCallback((_1, reason) => {
    if (multiple) {
      onClose(reason);
      if (reason !== "select-option") {
        dispatchAction({
          type: "close"
        });
      }
    } else {
      dispatchAction({
        type: "close"
      });
    }
    onClose();
  }, [multiple, onClose]);
  const handleOnChange = useCallback((_1, newValue) => {
    if (typeof inputValue === "undefined") {
      dispatchAction({
        type: "onChange",
        valueComponent: newValue
      });
      setValueComponent(newValue, multiple, setValue, onChangeSelect, onChangeMultipleSelect);
    } else if (isMultipleValue(newValue, multiple)) {
      onChangeMultipleSelect(newValue);
    } else {
      onChangeSelect(newValue);
    }
  }, [inputValue, multiple, setValue, onChangeSelect, onChangeMultipleSelect]);
  useEffect(() => {
    if (resetForm && !isEqual(defaultValueComponent, state.valueComponent)) {
      dispatchAction({
        type: "onChange",
        valueComponent: defaultValueComponent
      });
    }
  }, [resetForm, defaultValueComponent, state]);
  const onPopperUpdate = useCallback(data => {
    if (state.flipped !== data.flipped) {
      dispatchAction({
        type: "changeFlipState",
        flipped: data.flipped
      });
    }
    const popperOptions = popperProps === null || popperProps === void 0 ? void 0 : popperProps.popperOptions;
    if (popperOptions && popperOptions.onUpdate) {
      popperOptions.onUpdate(data);
    }
  }, [popperProps, state.flipped]);
  const parsedPopperProps = useMemo(() => {
    const updatePopperOptions = {
      onUpdate: onPopperUpdate
    };
    return Object.assign(Object.assign({}, popperProps), {
      popperOptions: merge({}, popperProps === null || popperProps === void 0 ? void 0 : popperProps.popperOptions, updatePopperOptions)
    });
  }, [popperProps, onPopperUpdate]);
  const popperComponent = useCallback(defaultPopperProps => React__default.createElement(PopperComponent, Object.assign({}, defaultPopperProps, {
    customProps: parsedPopperProps
  })), [parsedPopperProps]);
  useEffect(() => {
    if (typeof dataResolver !== "function") {
      dispatchAction({
        type: "setOptions",
        options: defaultOptions
      });
    }
  }, [defaultOptions, dataResolver]);
  if (readOnly || disabled) {
    return React__default.createElement(InputComponent, {
      name: "readOnlyInput",
      value: getSelectedValue(state.valueComponent),
      required: required,
      readOnly: readOnly,
      label: label,
      disabled: disabled
    });
  }
  return React__default.createElement(Autocomplete, Object.assign({
    getOptionLabel: option => typeof option === "object" ? option.value : option.toString(),
    getOptionSelected: (option, newValue) => (typeof option === "object" ? option.id : option) === (typeof newValue === "object" ? newValue.id : newValue),
    loading: state.loading,
    loadingText: message.loading,
    noOptionsText: state.noOptionsMessage,
    filterSelectedOptions: filterSelectedOptions,
    open: state.open,
    inputValue: state.inputValue,
    options: state.options.map(option => {
      if (typeof option === "object") {
        return option;
      }
      return {
        id: option,
        value: option.toString()
      };
    }),
    classes: {
      option: classes.options,
      popper: clsx(classes.dropdown, {
        [classes.dropdownMobileFullScreen]: (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.enable) || state.open
      }),
      tag: classes.tag,
      groupUl: classes.groupUl,
      groupLabel: classes.groupLabel,
      popupIndicator: classes.popupIndicator,
      noOptions: classes.noOptions,
      loading: classes.loading,
      clearIndicator: classes.clearIndicator,
      clearIndicatorDirty: classes.clearIndicatorDirty
    },
    className: classes.formControl,
    onOpen: handleOpen,
    onClose: handleClose,
    /* needed because there are other generics that modify these properties internally (like free solo) */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: handleOnChange,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: state.valueComponent,
    ListboxProps: {
      onScroll: scrollCallback
    },
    onInputChange: handleOnInputChange,
    PopperComponent: popperComponent,
    blurOnSelect: blurOnSelect,
    renderTags: renderTags,
    renderInput: params => {
      const defaultEndAdornment = React__default.createElement(React__default.Fragment, null, state.loading ? React__default.createElement(CircularProgress, {
        color: "primary",
        size: 20
      }) : null, params.InputProps.endAdornment);
      const endAdornment = endAdornments ? endAdornments(defaultEndAdornment) : defaultEndAdornment;
      const CloseIconComponent = (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.closeIcon) || ArrowBack;
      const defaultStartAdornments = React__default.createElement(React__default.Fragment, null, mobileFullScreenMode && state.open && React__default.createElement(IconButton, {
        onClick: handleClose,
        className: classes.mobileFullScreenCloseIcon
      }, React__default.createElement(CloseIconComponent, null)), params.InputProps.startAdornment);
      const startAdornment = startAdornments ? startAdornments(defaultStartAdornments) : defaultStartAdornments;
      return React__default.createElement(TextField, Object.assign({
        required: required,
        label: label ? React__default.createElement("span", null, label) : undefined,
        name: name,
        error: error,
        classes: {
          root: classes.textField
        },
        SelectProps: {
          disableUnderline: true
        },
        onClick: onClick
      }, params, {
        InputLabelProps: Object.assign({
          shrink: "shrink" in props ? inputShrink : true,
          focused: readOnly ? false : undefined,
          classes: {
            root: clsx({
              [classes.readonlyLabel]: readOnly
            }),
            focused: classes.focused,
            error: classes.errorLabel,
            asterisk: classes.asterisk,
            shrink: classes.labelShrink
          }
        }, params.InputLabelProps),
        InputProps: Object.assign(Object.assign(Object.assign(Object.assign({
          disableUnderline: readOnly || disableUnderline,
          classes: {
            root: clsx(classes.input, {
              [classes.open]: state.open && !state.flipped,
              [classes.openFlipped]: state.open && state.flipped,
              [classes.readonlyInput]: readOnly,
              [classes.inputMobileFullScreen]: (mobileFullScreen === null || mobileFullScreen === void 0 ? void 0 : mobileFullScreen.enable) && state.open
            }),
            focused: classes.focused,
            error: classes.errorInput,
            underline: classes.underline
          }
        }, params.InputProps), {
          endAdornment: readOnly ? null : endAdornment,
          startAdornment: readOnly ? null : startAdornment
        }), inputParams), {
          readOnly,
          placeholder: readOnly && !value ? "-" : inputParams === null || inputParams === void 0 ? void 0 : inputParams.placeholder,
          onFocus: handleFocus
        }),
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps: Object.assign(Object.assign({}, params.inputProps), {
          autoComplete: autoCompleteOverride || "off"
        }),
        helperText: errorMessage,
        FormHelperTextProps: {
          className: classes.formHelperText
        }
      }));
    }
  }, config === null || config === void 0 ? void 0 : config.selectConfig, {
    renderOption: renderOption,
    multiple: multiple,
    ChipProps: ChipProps,
    disableClearable: readOnly ? true : (_a = config === null || config === void 0 ? void 0 : config.selectConfig) === null || _a === void 0 ? void 0 : _a.disableClearable,
    popupIcon: dropdownIcon,
    closeIcon: React__default.createElement(CloseIcon, null),
    filterOptions: filterOptions,
    getOptionDisabled: getOptionDisabled,
    freeSolo: freeSolo,
    openOnFocus: openOnFocus,
    autoHighlight: autoHighlight,
    disableCloseOnSelect: disableCloseOnSelect
  }));
};
const isMultipleValue = (value, multiple) => multiple;
const getResultCache = (resultsCache, _ref2) => {
  let {
    search,
    page = 1
  } = _ref2;
  return resultsCache.find(_ref3 => {
    let {
      search: searchItem,
      page: pageItem = 1
    } = _ref3;
    return searchItem === search && pageItem === page;
  });
};
const getSelectedValue = selectedValue => {
  let result = "";
  if (Array.isArray(selectedValue)) {
    result = selectedValue.map(option => typeof option === "object" ? option.value : option).join(", ");
  } else if (typeof selectedValue === "object") {
    result = (selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.value.toString()) || "";
  } else if (selectedValue) {
    result = selectedValue.toString();
  }
  return result;
};
const parseValue$1 = (value, dataResolver, multiple) => {
  if (isSelectOption(value) || Array.isArray(value)) {
    return value;
  }
  if (value && Array.isArray(dataResolver)) {
    // eslint-disable-next-line eqeqeq
    const index = dataResolver.findIndex(option => option.id == value);
    if (index > -1) {
      return dataResolver[index];
    }
  }
  return multiple ? [] : null;
};
const setValueComponent = function (value, multiple, setValue) {
  let onChangeSelect = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : noop;
  let onChangeMultipleSelect = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : noop;
  if (isMultipleValue(value, multiple)) {
    if (value && value.length) {
      const newValue = value.map(option => typeof option === "object" ? option.id : option);
      setValue(previousState => isEqual(newValue, previousState) ? previousState : newValue // since .map creates a new array, evaluate if is equal to the previous one
      );
      onChangeMultipleSelect(value);
    } else {
      setValue(null);
      onChangeMultipleSelect([]);
    }
  } else if (value) {
    setValue(typeof value === "object" ? value.id : value);
    onChangeSelect(value);
  } else {
    setValue(null);
    onChangeSelect(null);
  }
};

const useDatePickerStyles = makeStyles(theme => {
  const breakpoints = getBreakpoints(theme);
  return {
    formControl: Object.assign({
      minWidth: "100px",
      width: "100%"
    }, getCss(theme, "formControl", "datePicker")),
    label: Object.assign(Object.assign({
      color: labelColor,
      "&.Mui-focused": {
        color: labelColor
      },
      fontSize: "14px"
    }, getCss(theme, "label.root", "datePicker")), {
      "&$labelFocused": Object.assign({}, getCss(theme, "label.focused", "datePicker")),
      "&$errorLabel": Object.assign({
        color: labelColor,
        "& $asterisk": {
          color: errorColor
        }
      }, getCss(theme, "label.error", "datePicker")),
      "&$labelShrink": Object.assign({}, getCss(theme, "label.shrink", "datePicker")),
      "&:hover": Object.assign({}, getCss(theme, "label.hover", "datePicker"))
    }),
    labelFocused: {},
    input: Object.assign(Object.assign({
      [breakpoints.desktop]: {
        height: "32px"
      },
      [breakpoints.mobile]: {
        height: "41px"
      }
    }, getCss(theme, "input.root", "datePicker")), {
      "&$focused": Object.assign({}, getCss(theme, "input.focused", "datePicker")),
      "&$errorInput": Object.assign({
        borderColor: errorColor,
        "& path": {
          fill: errorColor
        }
      }, getCss(theme, "input.error", "datePicker")),
      "&:hover": Object.assign({}, getCss(theme, "input.hover", "datePicker")),
      "&$underline:before": Object.assign({}, getCss(theme, "input.underline.root", "datePicker")),
      "&$underline:hover:before": Object.assign({}, getCss(theme, "input.underline.hover", "datePicker")),
      "&$underline:after": Object.assign({}, getCss(theme, "input.underline.active", "datePicker"))
    }),
    focused: {},
    errorLabel: {},
    errorInput: {},
    asterisk: Object.assign({}, getCss(theme, "label.asterisk", "datePicker")),
    required: Object.assign({}, getCss(theme, "label.required", "datePicker")),
    formHelperText: Object.assign({
      fontSize: "11px",
      lineHeight: "11px",
      marginLeft: "15px",
      "&.Mui-error": {
        color: errorColor
      }
    }, getCss(theme, "input.helperText", "datePicker")),
    underline: {},
    labelShrink: {}
  };
});
const getDefaultDatePickerStyle = () => {
  const theme = createTheme();
  return Object.assign(Object.assign({}, theme), {
    datePicker: {
      selectedDay: {
        color: theme.palette.primary.main
      }
    }
  });
};
const applyDatePickerThemeTransform = theme => {
  var _a, _b;
  const datePicker = (_b = (_a = theme === null || theme === void 0 ? void 0 : theme.forms) === null || _a === void 0 ? void 0 : _a.datePicker) === null || _b === void 0 ? void 0 : _b.pickerComponent;
  const defaultDatePickerStyle = {
    datePicker: {
      selectedDay: {
        color: theme.palette.primary.main
      }
    }
  };
  return merge({}, theme, defaultDatePickerStyle, {
    datePicker
  });
};

const useBaseDatePickerProps = (defaultValue, props, onReset) => {
  const {
      required = false
    } = props,
    otherProps = __rest(props, ["required"]);
  const {
      name,
      label,
      disableUnderline = true,
      shrink: inputShrink,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      validations = {},
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      defaultMessages,
      disabled = false,
      InputProps,
      InputLabelProps,
      keyboardMode,
      onFocus,
      id,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      mountFieldWhileDisabled
    } = otherProps,
    // do not inject in dom
    datePickerProps = __rest(otherProps, ["name", "label", "disableUnderline", "shrink", "validations", "defaultMessages", "disabled", "InputProps", "InputLabelProps", "keyboardMode", "onFocus", "id", "mountFieldWhileDisabled"]);
  const parsedId = useMemo(() => id || IdGenerator(), [id]);
  const {
    setValue,
    errorMessage,
    error,
    resetForm,
    value,
    readOnly,
    forceDisabledStyleForViewMode,
    setTouched
  } = useFormContext(otherProps, defaultValue);
  const classes = useDatePickerStyles();
  useEffect(() => {
    if (resetForm) {
      onReset();
    }
  }, [resetForm, defaultValue, value, onReset]);
  const keyboardModeProp = readOnly || disabled ? false : keyboardMode;
  const handleOnFocus = useCallback(e => {
    if (setTouched) {
      setTouched(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  }, [onFocus, setTouched]);
  return {
    setValue,
    readOnly,
    pickerProps: Object.assign(Object.assign({
      name,
      label,
      required,
      disabled,
      error,
      className: classes.formControl,
      onFocus: handleOnFocus,
      InputProps: Object.assign(Object.assign({
        disableUnderline: disableUnderline || readOnly,
        classes: {
          root: clsx(classes.input, {
            [classes.readonlyInput]: readOnly && !forceDisabledStyleForViewMode,
            [classes.disabled]: readOnly && forceDisabledStyleForViewMode
          }),
          focused: classes.focused,
          disabled: classes.disabled,
          error: classes.errorInput,
          underline: classes.underline
        },
        id: parsedId
      }, InputProps), {
        readOnly
      }),
      InputLabelProps: Object.assign(Object.assign({
        shrink: "shrink" in props && !readOnly ? inputShrink : true,
        classes: {
          root: clsx(classes.label, {
            [classes.readonlyLabel]: readOnly && !forceDisabledStyleForViewMode
          }),
          focused: classes.focused,
          disabled: classes.disabled,
          required: classes.required,
          asterisk: classes.asterisk,
          error: classes.errorLabel,
          shrink: classes.labelShrink
        },
        htmlFor: parsedId
      }, InputLabelProps), {
        focused: readOnly ? false : undefined
      }),
      helperText: errorMessage,
      FormHelperTextProps: {
        className: classes.formHelperText
      },
      keyboardMode: keyboardModeProp
    }, datePickerProps), {
      readOnly,
      placeholder: readOnly || disabled ? "-" : datePickerProps === null || datePickerProps === void 0 ? void 0 : datePickerProps.placeholder
    })
  };
};
const defaultInternalDateFormat = "yyyy-MM-dd";
const defaultInternalDateTimeFormat = "yyyy-MM-dd HH:mm";
const defaultInternalTimeFormat = "HH:mm";

const DatePicker = inputProps => {
  const {
      formatValue,
      onChangeDate,
      defaultValue,
      value,
      minDateMessage,
      maxDateMessage,
      invalidDateMessage,
      minDate,
      maxDate,
      defaultMessages: defaultMessages$1,
      disabled
    } = inputProps,
    props = __rest(inputProps, ["formatValue", "onChangeDate", "defaultValue", "value", "minDateMessage", "maxDateMessage", "invalidDateMessage", "minDate", "maxDate", "defaultMessages", "disabled"]);
  const utils = useUtils();
  const outerTheme = useTheme$1();
  const defaultValueFormatted = useMemo(() => baseFormatValue$3(typeof value === "undefined" ? defaultValue || null : value, utils, formatValue), [defaultValue, formatValue, utils, value]);
  const {
    defaultMessages: baseDefaultMessages
  } = useFormContext();
  const minDateValidation = useCallback(currentValue => {
    if (currentValue) {
      const date = utils.parse(currentValue, defaultInternalDateFormat);
      if (minDate && date && date < minDate) {
        return (minDateMessage === null || minDateMessage === void 0 ? void 0 : minDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.minDate) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.minDate) || defaultMessages.minDate;
      }
    }
    return true;
  }, [minDate, minDateMessage, defaultMessages$1]);
  const maxDateValidation = useCallback(currentValue => {
    if (currentValue) {
      const date = utils.parse(currentValue, defaultInternalDateFormat);
      if (maxDate && date && date > maxDate) {
        return (maxDateMessage === null || maxDateMessage === void 0 ? void 0 : maxDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.maxDate) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.maxDate) || defaultMessages.maxDate;
      }
    }
    return true;
  }, [maxDate, maxDateMessage, defaultMessages$1]);
  const currentValueRef = useRef(defaultValue);
  const validateDate = useCallback(currentValue => {
    if (value || currentValueRef.current) {
      const date = currentValueRef.current || utils.parse(currentValue, defaultInternalDateFormat);
      if (date && !utils.isValid(currentValue)) {
        return (invalidDateMessage === null || invalidDateMessage === void 0 ? void 0 : invalidDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.invalidDate) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.invalidDate) || defaultMessages.invalidDate;
      }
    } else if (inputProps.required) {
      return (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.required) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.required);
    }
    return true;
  }, [defaultMessages$1, invalidDateMessage, inputProps.required]);
  const validations = useMemo(() => {
    const validations = props.validations || {};
    let validate = validations.validate || {};
    if (typeof validate === "function") {
      validate = {
        customValidation: validate
      };
    }
    validate.minDate = minDateValidation;
    validate.maxDate = maxDateValidation;
    validate.validDate = validateDate;
    return {
      validations: Object.assign(Object.assign({}, props.validations), {
        validate
      })
    };
  }, [props.validations, minDateValidation, maxDateValidation, validateDate]);
  const parsedProps = useMemo(() => {
    return Object.assign(Object.assign({}, props), validations);
  }, [validations, props]);
  const [resetCounter, setResetCounter] = useState(1);
  const onReset = useCallback(() => {
    currentValueRef.current = defaultValue;
    setResetCounter(currentValue => currentValue + 1);
  }, [defaultValue]);
  const {
    setValue = noop,
    pickerProps,
    readOnly
  } = useBaseDatePickerProps(defaultValueFormatted, parsedProps, onReset);
  useEffect(() => {
    if (typeof value !== "undefined") {
      setValue(baseFormatValue$3(value, utils, formatValue));
      currentValueRef.current = value;
    }
  }, [value]);
  const handleOnChange = useCallback(date => {
    if (typeof value === "undefined") {
      setValue(baseFormatValue$3(date, utils, formatValue));
      currentValueRef.current = date;
    }
    if (onChangeDate) {
      onChangeDate(date);
    }
  }, [setValue, onChangeDate, formatValue]);
  // eslint-disable-next-line react/destructuring-assignment
  if (readOnly || disabled && !inputProps.mountFieldWhileDisabled) {
    const {
      label,
      required,
      format
    } = inputProps;
    return React__default.createElement(InputComponent, {
      name: "readOnlyInput",
      value: currentValueRef.current ? utils.format(currentValueRef.current, format || "dd/MM/yyyy") : currentValueRef.current,
      required: required,
      readOnly: readOnly,
      label: label,
      disabled: disabled
    });
  }
  const dateComponent = React__default.createElement(DatePicker$1, Object.assign({
    key: resetCounter,
    defaultValue: typeof value === "undefined" ? currentValueRef.current : undefined,
    onChange: handleOnChange,
    value: value,
    minDate: minDate,
    maxDate: maxDate
  }, pickerProps));
  return React__default.createElement(ThemeProvider, {
    theme: outerTheme ? applyDatePickerThemeTransform : getDefaultDatePickerStyle()
  }, dateComponent);
};
const baseFormatValue$3 = (date, utils, formatValue) => {
  if (formatValue) {
    return formatValue(date);
  }
  return date && utils.isValid(date) ? utils.format(date, defaultInternalDateFormat) : "";
};

const RangePicker = _a => {
  var {
      formatValue,
      onPartialRangeChange = noop,
      defaultValue,
      defaultMessages: defaultMessages$1,
      invalidDateMessage,
      disabled
    } = _a,
    props = __rest(_a, ["formatValue", "onPartialRangeChange", "defaultValue", "defaultMessages", "invalidDateMessage", "disabled"]);
  const utils = useUtils();
  const outerTheme = useTheme$1();
  const defaultValueFormatted = useMemo(() => baseFormatValue$2(defaultValue || null, utils, formatValue), [formatValue, defaultValue]);
  const currentValueRef = useRef(defaultValue);
  const {
    defaultMessages: baseDefaultMessages
  } = useFormContext();
  const validateDates = useCallback(value => {
    var _a, _b;
    if (value && Array.isArray(value) || currentValueRef.current) {
      const startDate = ((_a = currentValueRef.current) === null || _a === void 0 ? void 0 : _a.startDate) || utils.parse(value[0], defaultInternalDateFormat);
      const endDate = ((_b = currentValueRef.current) === null || _b === void 0 ? void 0 : _b.endDate) || utils.parse(value[1], defaultInternalDateFormat);
      if (startDate && !utils.isValid(startDate) || endDate && !utils.isValid(endDate)) {
        return (invalidDateMessage === null || invalidDateMessage === void 0 ? void 0 : invalidDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.invalidDate) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.invalidDate) || defaultMessages.invalidDate;
      }
    } else if (props.required) {
      return (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.required) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.required);
    }
    return true;
  }, [defaultMessages$1, invalidDateMessage, props.required]);
  const validations = useMemo(() => {
    const validations = props.validations || {};
    let validate = validations.validate || {};
    if (typeof validate === "function") {
      validate = {
        customValidation: validate
      };
    }
    validate.validDates = validateDates;
    return {
      validations: Object.assign(Object.assign({}, props.validations), {
        validate
      })
    };
  }, [props.validations, validateDates]);
  const parsedProps = useMemo(() => {
    return Object.assign(Object.assign({}, props), validations);
  }, [validations, props]);
  const [resetCounter, setResetCounter] = useState(1);
  const onReset = useCallback(() => {
    currentValueRef.current = defaultValue;
    setResetCounter(currentValue => currentValue + 1);
  }, [defaultValue]);
  const {
    setValue,
    pickerProps,
    readOnly
  } = useBaseDatePickerProps(defaultValueFormatted, parsedProps, onReset);
  const handleOnChange = useCallback(date => {
    const {
      startDate,
      endDate
    } = date;
    const parsedDate = startDate && endDate ? date : null;
    if (setValue && (startDate && endDate || !startDate && !endDate)) {
      setValue(baseFormatValue$2(parsedDate, utils, formatValue));
      currentValueRef.current = parsedDate;
    }
    if (onPartialRangeChange) {
      onPartialRangeChange(date);
    }
  }, [setValue, onPartialRangeChange, formatValue]);
  // eslint-disable-next-line react/destructuring-assignment
  if (readOnly || disabled && !props.mountFieldWhileDisabled) {
    const {
      label,
      required,
      format
    } = props;
    return React__default.createElement(InputComponent, {
      name: "readOnlyInput",
      value: currentValueRef.current ? `${utils.format(currentValueRef.current.startDate, format || "dd/MM/yyyy")} - ${utils.format(currentValueRef.current.endDate, format || "dd/MM/yyyy")}` : currentValueRef.current,
      required: required,
      readOnly: readOnly,
      label: label,
      disabled: disabled
    });
  }
  const dateComponent = React__default.createElement(RangePicker$1, Object.assign({
    key: resetCounter,
    defaultValue: currentValueRef.current,
    onPartialRangeChange: handleOnChange
  }, pickerProps));
  return React__default.createElement(ThemeProvider, {
    theme: outerTheme ? applyDatePickerThemeTransform : getDefaultDatePickerStyle()
  }, dateComponent);
};
const baseFormatValue$2 = (date, utils, formatValue) => {
  if (formatValue) {
    return formatValue(date);
  }
  if (date) {
    return [utils.format(date.startDate, defaultInternalDateFormat), utils.format(date.endDate, defaultInternalDateFormat)];
  }
  return "";
};

const DateTimePicker = inputProps => {
  const {
      formatValue,
      onChangeDate,
      defaultValue,
      value,
      minDateMessage,
      maxDateMessage,
      invalidDateMessage,
      minDate,
      maxDate,
      defaultMessages: defaultMessages$1,
      disabled
    } = inputProps,
    props = __rest(inputProps, ["formatValue", "onChangeDate", "defaultValue", "value", "minDateMessage", "maxDateMessage", "invalidDateMessage", "minDate", "maxDate", "defaultMessages", "disabled"]);
  const utils = useUtils();
  const outerTheme = useTheme$1();
  const defaultValueFormatted = useMemo(() => baseFormatValue$1(typeof value === "undefined" ? defaultValue || null : value, utils, formatValue), [defaultValue, formatValue, value]);
  const {
    defaultMessages: baseDefaultMessages
  } = useFormContext();
  const minDateValidation = useCallback(value => {
    if (value) {
      const date = utils.parse(value, defaultInternalDateTimeFormat);
      if (minDate && date && date < minDate) {
        return (minDateMessage === null || minDateMessage === void 0 ? void 0 : minDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.minDateTime) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.minDateTime) || defaultMessages.minDateTime;
      }
    }
    return true;
  }, [minDate, minDateMessage, defaultMessages$1]);
  const maxDateValidation = useCallback(value => {
    if (value) {
      const date = utils.parse(value, defaultInternalDateTimeFormat);
      if (maxDate && date && date > maxDate) {
        return (maxDateMessage === null || maxDateMessage === void 0 ? void 0 : maxDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.maxDateTime) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.maxDateTime) || defaultMessages.maxDateTime;
      }
    }
    return true;
  }, [maxDate, maxDateMessage, defaultMessages$1]);
  const currentValueRef = useRef(defaultValue);
  const validateDate = useCallback(value => {
    if (value || currentValueRef.current) {
      const date = currentValueRef.current || utils.parse(value, defaultInternalDateTimeFormat);
      if (date && !utils.isValid(value)) {
        return (invalidDateMessage === null || invalidDateMessage === void 0 ? void 0 : invalidDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.invalidDateTime) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.invalidDateTime) || defaultMessages.invalidDateTime;
      }
    } else if (inputProps.required) {
      return (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.required) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.required);
    }
    return true;
  }, [defaultMessages$1, invalidDateMessage, inputProps.required]);
  const validations = useMemo(() => {
    const validations = props.validations || {};
    let validate = validations.validate || {};
    if (typeof validate === "function") {
      validate = {
        customValidation: validate
      };
    }
    validate.minDate = minDateValidation;
    validate.maxDate = maxDateValidation;
    validate.validDate = validateDate;
    return {
      validations: Object.assign(Object.assign({}, props.validations), {
        validate
      })
    };
  }, [props.validations, minDateValidation, maxDateValidation, validateDate]);
  const parsedProps = useMemo(() => {
    return Object.assign(Object.assign({}, props), validations);
  }, [validations, props]);
  const [resetCounter, setResetCounter] = useState(1);
  const onReset = useCallback(() => {
    currentValueRef.current = defaultValue;
    setResetCounter(currentValue => currentValue + 1);
  }, [defaultValue]);
  const {
    setValue = noop,
    pickerProps,
    readOnly
  } = useBaseDatePickerProps(defaultValueFormatted, parsedProps, onReset);
  useEffect(() => {
    if (typeof value !== "undefined") {
      setValue(baseFormatValue$1(value, utils, formatValue));
      currentValueRef.current = value;
    }
  }, [value]);
  const handleOnChange = useCallback(date => {
    if (typeof value === "undefined") {
      setValue(baseFormatValue$1(date, utils, formatValue));
      currentValueRef.current = date;
    }
    if (onChangeDate) {
      onChangeDate(date);
    }
  }, [setValue, onChangeDate, formatValue]);
  // eslint-disable-next-line react/destructuring-assignment
  if (readOnly || disabled && !inputProps.mountFieldWhileDisabled) {
    const {
      label,
      required,
      format
    } = inputProps;
    return React__default.createElement(InputComponent, {
      name: "readOnlyInput",
      value: currentValueRef.current ? utils.format(currentValueRef.current, format || "dd/MM/yyyy HH:mm") : currentValueRef.current,
      required: required,
      readOnly: readOnly,
      label: label,
      disabled: disabled
    });
  }
  const dateComponent = React__default.createElement(DateTimePicker$1, Object.assign({
    key: resetCounter,
    defaultValue: typeof value === "undefined" ? currentValueRef.current : undefined,
    onChange: handleOnChange,
    value: value,
    minDate: minDate,
    maxDate: maxDate
  }, pickerProps));
  return React__default.createElement(ThemeProvider, {
    theme: outerTheme ? applyDatePickerThemeTransform : getDefaultDatePickerStyle()
  }, dateComponent);
};
const baseFormatValue$1 = (date, utils, formatValue) => {
  if (formatValue) {
    return formatValue(date);
  }
  return date && utils.isValid(date) ? utils.format(date, defaultInternalDateTimeFormat) : "";
};

const TimePicker = inputProps => {
  const {
      formatValue,
      onChangeDate,
      defaultValue,
      value,
      minDateMessage,
      maxDateMessage,
      invalidDateMessage,
      minDate,
      maxDate,
      defaultMessages: defaultMessages$1,
      disabled
    } = inputProps,
    props = __rest(inputProps, ["formatValue", "onChangeDate", "defaultValue", "value", "minDateMessage", "maxDateMessage", "invalidDateMessage", "minDate", "maxDate", "defaultMessages", "disabled"]);
  const utils = useUtils();
  const outerTheme = useTheme$1();
  const defaultValueFormatted = useMemo(() => baseFormatValue(parseValue(typeof value === "undefined" ? defaultValue || null : value), utils, formatValue), [defaultValue, formatValue, value]);
  const {
    defaultMessages: baseDefaultMessages
  } = useFormContext();
  const minDateValidation = useCallback(value => {
    if (value) {
      const date = utils.parse(value, defaultInternalTimeFormat);
      const parsedMinDate = minDate ? parseValue(minDate) : undefined;
      if (parsedMinDate && date && date < parsedMinDate) {
        const message = (minDateMessage === null || minDateMessage === void 0 ? void 0 : minDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.minTime) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.minTime) || defaultMessages.minTime;
        return message.replace("{{1}}", baseFormatValue(parsedMinDate, utils, formatValue));
      }
    }
    return true;
  }, [minDate, minDateMessage, defaultMessages$1, formatValue]);
  const maxDateValidation = useCallback(value => {
    if (value) {
      const date = utils.parse(value, defaultInternalTimeFormat);
      const parsedMaxDate = maxDate ? parseValue(maxDate) : undefined;
      if (parsedMaxDate && date && date > parsedMaxDate) {
        const message = (maxDateMessage === null || maxDateMessage === void 0 ? void 0 : maxDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.maxTime) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.maxTime) || defaultMessages.maxTime;
        return message.replace("{{1}}", baseFormatValue(parsedMaxDate, utils, formatValue));
      }
    }
    return true;
  }, [maxDate, maxDateMessage, defaultMessages$1]);
  const currentValueRef = useRef(defaultValue ? parseValue(defaultValue) : defaultValue);
  const validateDate = useCallback(value => {
    if (value || currentValueRef.current) {
      const date = currentValueRef.current || utils.parse(value, defaultInternalTimeFormat);
      if (date && !utils.isValid(date)) {
        return (invalidDateMessage === null || invalidDateMessage === void 0 ? void 0 : invalidDateMessage.toString()) || (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.invalidTime) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.invalidTime) || defaultMessages.invalidTime;
      }
    } else if (inputProps.required) {
      return (defaultMessages$1 === null || defaultMessages$1 === void 0 ? void 0 : defaultMessages$1.required) || (baseDefaultMessages === null || baseDefaultMessages === void 0 ? void 0 : baseDefaultMessages.required);
    }
    return true;
  }, [defaultMessages$1, invalidDateMessage, inputProps.required]);
  const validations = useMemo(() => {
    const validations = props.validations || {};
    let validate = validations.validate || {};
    if (typeof validate === "function") {
      validate = {
        customValidation: validate
      };
    }
    validate.minDate = minDateValidation;
    validate.maxDate = maxDateValidation;
    validate.validDate = validateDate;
    return {
      validations: Object.assign(Object.assign({}, props.validations), {
        validate
      })
    };
  }, [props.validations, minDateValidation, maxDateValidation, validateDate]);
  const parsedProps = useMemo(() => {
    return Object.assign(Object.assign({}, props), validations);
  }, [validations, props]);
  const [resetCounter, setResetCounter] = useState(1);
  const onReset = useCallback(() => {
    currentValueRef.current = defaultValue ? parseValue(defaultValue) : defaultValue;
    setResetCounter(currentValue => currentValue + 1);
  }, [defaultValue]);
  const {
    setValue = noop,
    pickerProps,
    readOnly
  } = useBaseDatePickerProps(defaultValueFormatted, parsedProps, onReset);
  useEffect(() => {
    if (typeof value !== "undefined") {
      const parsedValue = parseValue(value);
      setValue(baseFormatValue(parsedValue, utils, formatValue));
      currentValueRef.current = parsedValue;
    }
  }, [value]);
  const handleOnChange = useCallback(date => {
    if (typeof value === "undefined") {
      setValue(baseFormatValue(date, utils, formatValue));
      currentValueRef.current = date;
    }
    if (onChangeDate) {
      onChangeDate(date);
    }
  }, [setValue, onChangeDate, formatValue]);
  // eslint-disable-next-line react/destructuring-assignment
  if (readOnly || disabled && !inputProps.mountFieldWhileDisabled) {
    const {
      label,
      required,
      format
    } = inputProps;
    return React__default.createElement(InputComponent, {
      name: "readOnlyInput",
      value: currentValueRef.current ? utils.format(currentValueRef.current, format || "HH:mm") : currentValueRef.current,
      required: required,
      readOnly: readOnly,
      label: label,
      disabled: disabled
    });
  }
  const dateComponent = React__default.createElement(TimePicker$1, Object.assign({
    key: resetCounter,
    defaultValue: typeof value === "undefined" ? currentValueRef.current : undefined,
    onChange: handleOnChange,
    value: value
  }, pickerProps));
  return React__default.createElement(ThemeProvider, {
    theme: outerTheme ? applyDatePickerThemeTransform : getDefaultDatePickerStyle()
  }, dateComponent);
};
const baseFormatValue = (date, utils, formatValue) => {
  if (formatValue) {
    return formatValue(date);
  }
  return date && utils.isValid(date) ? utils.format(date, defaultInternalTimeFormat) : "";
};
const parseValue = value => {
  if (value && istTimeObject(value)) {
    const date = new Date();
    date.setHours(value.hour);
    date.setMinutes(value.minute);
    return date;
  }
  return value;
};
const istTimeObject = value => "hour" in value && "minute" in value;

const useWatch = (name, defaultValue) => {
  const {
    control
  } = useFormContext();
  return useWatch$1({
    name,
    control,
    defaultValue
  });
};
const useWatchMultiple = (name, defaultValue) => {
  const {
    control
  } = useFormContext();
  return useWatch$1({
    name,
    control,
    defaultValue
  });
};

export { Checkbox, DatePicker, DateTimePicker, FileUploader, Form, InputComponent as Input, InputEmail, InputNumber$1 as InputNumber, InputNumber as InputPassword, RadioComponent as Radio, RangePicker, SelectComponent as Select, SelectSearch, SwitchComponent as Switch, Textarea, TimePicker, getCss, isBaseFile, isFileWithId, isSelectOption, useFormContext, useWatch, useWatchMultiple };
//# sourceMappingURL=main.esm.js.map
